/**
 * API Service - Backend Communication
 * Handles all API calls to the Azure Functions backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Token management
let authToken = localStorage.getItem('authToken');

// Decode a base64url-encoded JWT payload to its JSON object.
// Correct padding is critical: when (length % 4) is 2 or 3, atob() throws on
// bad padding, which would otherwise force-log-out the user on every page load.
function decodeJwtPayload(payload) {
  const padLen = (4 - (payload.length % 4)) % 4;
  const b64 = payload.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(padLen);
  return JSON.parse(atob(b64));
}

// Proactively discard expired tokens on module load so the app
// never starts in a half-authenticated state.
(function pruneExpiredToken() {
  if (!authToken) return;
  try {
    const [payload] = authToken.split('.');
    const { exp } = decodeJwtPayload(payload);
    if (exp && Date.now() > exp) {
      localStorage.removeItem('authToken');
      authToken = null;
    }
  } catch {
    // Malformed token — discard it
    localStorage.removeItem('authToken');
    authToken = null;
  }
})();

export function setAuthToken(token) {
  authToken = token;
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
}

export function getAuthToken() {
  return authToken;
}

export function isAuthenticated() {
  return !!authToken;
}

/**
 * Decode the JWT-style payload from the stored token without verifying the signature.
 * Returns { userId, email, role, tier, exp } or null if token is missing/invalid.
 */
export function decodeCurrentUser() {
  if (!authToken) return null;
  try {
    const [payload] = authToken.split('.');
    const decoded = decodeJwtPayload(payload);
    return {
      userId: decoded.userId || 'admin',
      email: decoded.email || 'admin@haythix.local',
      role: decoded.role || 'admin',
      tier: decoded.tier || 'elite',
      mfaEnabled: decoded.mfaEnabled || false,
      jti: decoded.jti || null,
      exp: decoded.exp,
    };
  } catch {
    return null;
  }
}

/**
 * Just the jti from the current session token — used by the admin Sessions
 * tab to flag "this device" so the operator doesn't accidentally revoke
 * the session they're currently signed in on.
 */
export function getCurrentSessionJti() {
  if (!authToken) return null;
  try {
    const [payload] = authToken.split('.');
    return decodeJwtPayload(payload).jti || null;
  } catch { return null; }
}

// Generic fetch wrapper
// Alias for the modern naming convention used in newer API functions
const apiRequest = (endpoint, options = {}) => fetchApi(endpoint, options);

async function fetchApi(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  // Attach the cached step-up token (if any) so sensitive endpoints get
  // past the requireStepUp gate without needing a re-prompt every call.
  // The token is short-lived (5 min); when it expires the backend returns
  // 401 + STEPUP_REQUIRED and the modal handles the refresh.
  try {
    const su = sessionStorage.getItem('stepUpToken');
    const suExp = parseInt(sessionStorage.getItem('stepUpExpiresAt') || '0', 10);
    if (su && Date.now() < suExp) {
      headers['X-Step-Up-Token'] = su;
    }
  } catch { /* sessionStorage unavailable — fine */ }

  // Hard timeout. Without this, a hung connection (cold start, dropped TCP,
  // service-worker fetch-handler quirk) leaves the page on a spinner forever.
  // 25s comfortably covers Azure Functions cold starts while still failing
  // fast enough that the UI can render an error and let the user retry.
  // Callers can override via options.timeoutMs (e.g. heavy backtests).
  const timeoutMs = options.timeoutMs ?? 25000;
  const controller = options.signal ? null : new AbortController();
  const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      signal: options.signal || controller?.signal,
    });
  } catch (err) {
    if (timeoutId) clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      const e = new Error(`Request timed out after ${timeoutMs}ms [${endpoint}]`);
      e.endpoint = endpoint;
      e.isTimeout = true;
      throw e;
    }
    throw err;
  }
  if (timeoutId) clearTimeout(timeoutId);

  // Some endpoints return non-JSON (e.g. proxied HTML when an upstream is
  // misconfigured). Guard the JSON parse so the user sees a meaningful error
  // rather than a generic "Unexpected token <" failure.
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    // 4-eyes approval gate — sensitive action created a pending approval.
    // 202 response carries the approval ID so the UI can link the user to
    // the Approvals tab to watch its status.
    if (response.status === 202 && data?.error?.code === 'APPROVAL_REQUIRED') {
      const err = new Error(data?.error?.message || 'Approval required');
      err.httpStatus = 202;
      err.code = 'APPROVAL_REQUIRED';
      err.approvalId = data?.error?.approvalId || null;
      err.action = data?.error?.action || null;
      err.summary = data?.error?.summary || null;
      err.endpoint = endpoint;
      throw err;
    }
    // Step-up auth re-challenge — NOT a logout signal. Sensitive endpoints
    // return 401 + code: STEPUP_REQUIRED to ask the caller to prove a fresh
    // credential. The step-up modal handles this; we just throw an error
    // the caller can catch by checking err.code === 'STEPUP_REQUIRED'.
    if (response.status === 401 && data?.error?.code === 'STEPUP_REQUIRED') {
      const err = new Error(data?.error?.message || 'Fresh authentication required');
      err.httpStatus = 401;
      err.code = 'STEPUP_REQUIRED';
      err.action = data?.error?.action || null;
      err.endpoint = endpoint;
      throw err;
    }
    // Handle 401 - unauthorized
    if (response.status === 401) {
      setAuthToken(null);
      // Critical: don't redirect to /login when ALREADY on a public page.
      // Without this guard, a stale token on the login page itself causes
      // an infinite reload loop:
      //   render → API call → 401 → href = '/login' → reload → render → ...
      // Public routes that should never trigger the redirect (must mirror
      // PublicRoute set in App.jsx).
      const path = window.location.pathname || '';
      // PUBLIC_PREFIXES: paths where a 401 must NOT trigger the auto-redirect
      // to /login. Without /accept-*-invite here, a stale token (from a prior
      // test signup) would cause AuthProvider's startup refresh to 401 → user
      // yanked to /login before the invite acceptance page ever renders.
      // Recipient then ends up creating a regular trial account instead of
      // becoming an admin.
      const PUBLIC_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password',
                                '/pricing', '/comparison', '/features', '/advertise',
                                '/analyze', '/wallet',
                                '/accept-admin-invite', '/accept-team-invite',
                                '/verify-email'];
      const onPublicPage = path === '/' || PUBLIC_PREFIXES.some(p => path === p || path.startsWith(p + '/') || path.startsWith(p + '?'));
      if (!onPublicPage) {
        window.location.href = '/login';
      }
    }
    // Read the error message from any of the shapes the backend actually uses:
    //   1. Nested: { error: { message: 'human readable' } }
    //   2. Flat:   { error: 'CODE', message: 'human readable' }
    //   3. Plain:  { error: 'just a string' }
    // Falling back to status text means we never hide the real failure as a
    // generic "API request failed" — that was hiding upstream rate-limit errors.
    const inner =
      data?.error?.message ||
      data?.message ||
      (typeof data?.error === 'string' ? data.error : null) ||
      response.statusText ||
      `HTTP ${response.status}`;
    // Always include status + path so users (and us debugging from screenshots)
    // can see exactly which call failed. Previously a generic "Internal Server
    // Error" gave us nothing to work with.
    const msg = `${inner} [HTTP ${response.status} from ${endpoint}]`;
    const err = new Error(msg);
    err.httpStatus = response.status;
    err.endpoint = endpoint;
    err.responseBody = data;
    throw err;
  }

  return data;
}

// ============================================================================
// Authentication
// ============================================================================

/**
 * Login — supports both:
 *   login({ email, password }) — registered user
 *   login({ password })        — legacy admin (password only)
 */
export async function login({ email, password } = {}) {
  const body = email ? { email, password } : { password };
  // Use raw fetch so a 401 doesn't auto-redirect on the login page itself
  const url = `${API_BASE_URL}/auth/login`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const response = await resp.json();
  const data = response.data || {};
  if (response.success && data.mfaPending) {
    return { success: true, mfaPending: true, mfaToken: data.mfaToken };
  }
  const token = data.token;
  if (response.success && token) {
    setAuthToken(token);
  }
  return response;
}

export async function verifyMfa({ mfaToken, code }) {
  const url = `${API_BASE_URL}/auth/mfa/verify`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mfaToken, code }),
  });
  const response = await resp.json();
  const token = response.data?.token;
  if (response.success && token) {
    setAuthToken(token);
  }
  return response;
}

export async function setupAdmin({ email, name, password }) {
  // Auth token sent automatically via fetchApi — no password over the wire
  const response = await fetchApi('/auth/setup-admin', {
    method: 'POST',
    body: JSON.stringify({ email, name, password }),
  });
  const token = response.data?.token;
  if (response.success && token) setAuthToken(token);
  return response;
}

export async function getMfaStatus() {
  const response = await fetchApi('/auth/mfa/status');
  return response.data;
}

export async function setupMfa() {
  const response = await fetchApi('/auth/mfa/setup', { method: 'POST', body: JSON.stringify({}) });
  return response.data;
}

export async function verifyMfaSetup({ secret, code }) {
  const response = await fetchApi('/auth/mfa/verify-setup', {
    method: 'POST',
    body: JSON.stringify({ secret, code }),
  });
  return response.data;
}

export async function regenerateMfaBackupCodes(code) {
  const response = await fetchApi('/auth/mfa/regenerate-backup-codes', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return response.data;
}

export async function disableMfa({ code }) {
  const response = await fetchApi('/auth/mfa/disable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return response.data;
}

export async function changePassword({ currentPassword, newPassword }) {
  const response = await fetchApi('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return response.data;
}

export async function register({ email, name, password, referredBy = null }) {
  const response = await fetchApi('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, name, password, referredBy }),
  });
  const token = response.token || response.data?.token;
  if (response.success && token) {
    setAuthToken(token);
  }
  return response;
}

export async function refreshAuthToken() {
  const response = await fetchApi('/auth/refresh', { method: 'POST' });
  const token = response.data?.token;
  if (response.success && token) {
    setAuthToken(token);
  }
  return response;
}

export async function logout() {
  try {
    await fetchApi('/auth/logout', { method: 'POST' });
  } finally {
    setAuthToken(null);
  }
}

// Update the signed-in user's profile (name, avatarUrl).
// Email/password are NOT editable here — they have dedicated endpoints.
export async function updateMyProfile({ name, avatarUrl } = {}) {
  const body = {};
  if (typeof name === 'string') body.name = name;
  if (typeof avatarUrl === 'string' || avatarUrl === null) body.avatarUrl = avatarUrl;
  return fetchApi('/auth/me', { method: 'PATCH', body: JSON.stringify(body) });
}

export async function sendEmailVerification() {
  const r = await fetchApi('/auth/send-verification', { method: 'POST' });
  return r?.data || r;
}

export async function verifyEmail(token) {
  const r = await fetchApi(`/auth/verify-email/${encodeURIComponent(token)}`);
  return r?.data || r;
}

// ============================================================================
// Subscription
// ============================================================================

export async function getSubscriptionStatus() {
  const response = await fetchApi('/subscriptions/status');
  return response.data;
}

export async function createCheckoutSession(tier, period = 'monthly') {
  const response = await fetchApi('/subscriptions/create-checkout', {
    method: 'POST',
    body: JSON.stringify({ tier, period }),
  });
  return response.data;
}

export async function getBillingPortal() {
  const response = await fetchApi('/subscriptions/portal', { method: 'POST' });
  return response.data;
}

export async function changeSubscriptionTier(tier) {
  const response = await fetchApi('/subscriptions/change-tier', {
    method: 'POST',
    body: JSON.stringify({ tier }),
  });
  return response.data;
}

export async function createTrialCheckout(tier = 'starter', period = 'monthly') {
  const response = await fetchApi('/subscriptions/trial-checkout', {
    method: 'POST',
    body: JSON.stringify({ tier, period }),
  });
  return response.data; // { checkoutUrl }
}

// ============================================================================
// Bot Management
// ============================================================================

export async function listBots() {
  const response = await fetchApi('/bots');
  return response.data;
}

export async function getBot(botId) {
  const response = await fetchApi(`/bots/${botId}`);
  return response.data;
}

export async function updateBot(botId, config) {
  const response = await fetchApi(`/bots/${botId}`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function createOrUpdateBot(botId, config) {
  const response = await fetchApi(`/bots/${botId}`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function getBotStatus(botId) {
  const response = await fetchApi(`/bots/${botId}/status`);
  return response.data;
}

export async function pauseBot(botId, reason = 'Manually paused') {
  const response = await fetchApi(`/bots/${botId}/pause`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return response.data;
}

export async function resumeBot(botId) {
  const response = await fetchApi(`/bots/${botId}/resume`, {
    method: 'POST',
  });
  return response.data;
}

export async function deleteBot(botId) {
  const response = await fetchApi(`/bots/${botId}`, { method: 'DELETE' });
  return response.data;
}

export async function cloneBot(botId) {
  const response = await fetchApi(`/bots/${botId}/clone`, { method: 'POST' });
  return response.data;
}

// ============================================================================
// Analytics
// ============================================================================

export async function getTradeHistory(botId, days = 7, limit = 100) {
  const response = await fetchApi(`/bots/${botId}/trades?days=${days}&limit=${limit}`);
  return response.data;
}

export async function getAnalytics(botId, days = 30) {
  const response = await fetchApi(`/bots/${botId}/analytics?days=${days}`);
  return response.data;
}

// ============================================================================
// System
// ============================================================================

export async function getSystemHealth() {
  const response = await fetchApi('/health');
  return response.data;
}

export async function triggerOrchestrator() {
  const response = await fetchApi('/orchestrator/trigger', {
    method: 'POST',
  });
  return response;
}

export async function triggerHealthCheck() {
  const response = await fetchApi('/health/check', {
    method: 'POST',
  });
  return response.data;
}

// ─── Admin → System overview + manual ops ────────────────────────────────────
// Settings: System Health + Operations panels use these. All endpoints require
// the caller's session to be an admin — backend returns 403 otherwise.

export async function getSystemOverview() {
  const response = await fetchApi('/mgmt/system-overview');
  return response.data;
}

export async function getMgmtAuditLog() {
  const response = await fetchApi('/mgmt/ops/audit-log');
  return response.data;
}

export async function mgmtTriggerForecastResolver() {
  const response = await fetchApi('/mgmt/ops/trigger-forecast', { method: 'POST' });
  return response.data;
}

export async function mgmtRefreshCcxt() {
  const response = await fetchApi('/mgmt/ops/refresh-ccxt', { method: 'POST' });
  return response.data;
}

export async function mgmtResendVerification(email) {
  const response = await fetchApi('/mgmt/ops/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return response.data;
}

// ─── Admin → Business KPIs ───────────────────────────────────────────────────
// Backs the /admin Business tab. All endpoints admin-only (return 403 for
// non-admin sessions). Treasury is server-cached 60s.

export async function getBusinessKpis() {
  const response = await fetchApi('/mgmt/business/kpis');
  return response.data;
}

export async function getTreasuryBalances() {
  const response = await fetchApi('/mgmt/business/treasury-balances');
  return response.data;
}

export async function getWorkerStatus() {
  const response = await fetchApi('/mgmt/business/worker-status');
  return response.data;
}

export async function getReferralsDashboard() {
  const response = await fetchApi('/mgmt/business/referrals');
  return response.data;
}

// ─── Paid Channels admin — additional wrappers used by /admin Paid-Channels tab.
// (getPaidChannelFlags, getPaidChannelPending, togglePaidChannelFlag,
//  approvePaidChannelApp, rejectPaidChannelApp, managePaidChannelAllowlist
//  already live further down this file — reuse those.)

export async function getPaidChannelFailedPayouts() {
  const response = await fetchApi('/paid-channels/admin/failed-payouts');
  return response;
}

export async function getPaidChannelTreasuryStatus() {
  const response = await fetchApi('/paid-channels/admin/treasury-status');
  return response;
}

export async function getPaidChannelActiveChannels() {
  const response = await fetchApi('/paid-channels/admin/active-channels');
  return response;
}

export async function retryPaidChannelPayout(subscriptionId, paymentId) {
  const response = await fetchApi('/paid-channels/admin/retry-payout', {
    method: 'POST',
    body: JSON.stringify({ subscriptionId, paymentId }),
  });
  return response;
}

export async function getPaidChannelDetail(channelId) {
  const response = await fetchApi(`/paid-channels/admin/channel/${encodeURIComponent(channelId)}`);
  return response;
}

// ─── Admin Team Administration (RBAC — Owner only) ───────────────────────────
// Distinct from the user-tier "team plan" API further down (/team/*) which
// is paused. These talk to /mgmt/team/* and manage platform admin staff.

export async function getAdminTeamRoles() {
  const response = await fetchApi('/mgmt/team/roles');
  return response.data;
}

export async function getAdminTeamMembers() {
  const response = await fetchApi('/mgmt/team/members');
  return response.data;
}

export async function updateAdminTeamMemberRoles(userId, roles) {
  const response = await fetchApi(`/mgmt/team/members/${encodeURIComponent(userId)}/roles`, {
    method: 'PATCH',
    body: JSON.stringify({ roles }),
  });
  return response.data;
}

export async function revokeAdminTeamMember(userId) {
  const response = await fetchApi(`/mgmt/team/members/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  return response.data;
}

export async function getAdminTeamInvites() {
  const response = await fetchApi('/mgmt/team/invites');
  return response.data;
}

export async function createAdminTeamInvite(email, roles) {
  const response = await fetchApi('/mgmt/team/invite', {
    method: 'POST',
    body: JSON.stringify({ email, roles }),
  });
  return response.data;
}

export async function revokeAdminTeamInvite(inviteId) {
  const response = await fetchApi(`/mgmt/team/invites/${encodeURIComponent(inviteId)}/revoke`, { method: 'POST' });
  return response.data;
}

// ─── Step-up authentication ──────────────────────────────────────────────────
// Mints a 5-minute step-up token after the admin proves fresh password/MFA.
// Cached in sessionStorage so subsequent sensitive calls in the same window
// don't need a re-prompt.

export async function performStepUp({ password, totpCode }) {
  const response = await fetchApi('/auth/step-up', {
    method: 'POST',
    body: JSON.stringify({ password, totpCode }),
  });
  const { stepUpToken, expiresAt } = response.data || {};
  if (stepUpToken && expiresAt) {
    try {
      sessionStorage.setItem('stepUpToken', stepUpToken);
      sessionStorage.setItem('stepUpExpiresAt', String(new Date(expiresAt).getTime()));
    } catch {}
  }
  return response.data;
}

export function clearStepUpToken() {
  try {
    sessionStorage.removeItem('stepUpToken');
    sessionStorage.removeItem('stepUpExpiresAt');
  } catch {}
}

export function hasFreshStepUp() {
  try {
    const exp = parseInt(sessionStorage.getItem('stepUpExpiresAt') || '0', 10);
    return Date.now() < exp;
  } catch { return false; }
}

// ─── Audit log v2 (filtered + CSV export) ────────────────────────────────────

export async function getFilteredAuditLog(params = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') qs.set(k, v);
  }
  const suffix = qs.toString() ? `?${qs}` : '';
  const response = await fetchApi(`/mgmt/audit-log${suffix}`);
  return response.data;
}

export function getAuditLogCsvUrl(params = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') qs.set(k, v);
  }
  const suffix = qs.toString() ? `?${qs}` : '';
  return `${API_BASE_URL}/mgmt/audit-log/export.csv${suffix}`;
}

// ─── Admin-wide active sessions ──────────────────────────────────────────────

export async function getAdminSessions() {
  const response = await fetchApi('/mgmt/admin-sessions');
  return response.data;
}

// ─── 4-eyes approvals ────────────────────────────────────────────────────────

export async function listApprovals(status, action) {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  if (action) qs.set('action', action);
  const suffix = qs.toString() ? `?${qs}` : '';
  const response = await fetchApi(`/mgmt/approvals${suffix}`);
  return response.data;
}

export async function decideApproval(id, decision, notes) {
  const response = await fetchApi(`/mgmt/approvals/${encodeURIComponent(id)}/decide`, {
    method: 'POST',
    body: JSON.stringify({ decision, notes }),
  });
  return response.data;
}

export async function revokeAdminSession(userId, { jti, allOther } = {}) {
  const body = allOther ? { allOther: true } : { jti };
  const response = await fetchApi(`/mgmt/admin-sessions/${encodeURIComponent(userId)}/revoke`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.data;
}

// Public — invite acceptance flow. Backend routes use /team-invite/ instead
// of /admin-invite/ because Azure Functions silently 404s routes starting
// with admin- (neighbouring routes register fine, only admin-* drops).
export async function validateAdminInvite(token) {
  const response = await fetchApi(`/team-invite/validate?token=${encodeURIComponent(token)}`);
  return response.data;
}

export async function acceptAdminInvite({ token, password, name }) {
  const response = await fetchApi('/team-invite/accept', {
    method: 'POST',
    body: JSON.stringify({ token, password, name }),
  });
  return response.data;
}

// ============================================================================
// Market Intelligence (Cap 1, 2, 3)
// ============================================================================

export async function getBotTrend(botId) {
  const response = await fetchApi(`/bots/${botId}/trend`);
  return response.data;
}

export async function getBotIndicators(botId, indicators = '') {
  const params = indicators ? `?indicators=${indicators}` : '';
  const response = await fetchApi(`/bots/${botId}/indicators${params}`);
  return response.data;
}

export async function getBotForecast(botId) {
  const response = await fetchApi(`/bots/${botId}/forecast`);
  return response.data;
}

export async function getMonteCarlo(botId, paths = 1000, steps = 24) {
  const response = await fetchApi(`/bots/${botId}/monte-carlo?paths=${paths}&steps=${steps}`);
  return response.data;
}

export async function getMarketRegime(botId) {
  const response = await fetchApi(`/bots/${botId}/regime`);
  return response.data;
}

export async function getBotSignal(botId) {
  const response = await fetchApi(`/bots/${botId}/signal`);
  return response.data;
}

export async function getSignalHistory(botId, days = 7) {
  const response = await fetchApi(`/bots/${botId}/signal-history?days=${days}`);
  return response.data;
}

export async function getSignalPerformance(botId) {
  const response = await fetchApi(`/bots/${botId}/signal-performance`);
  return response.data;
}

// ============================================================================
// Strategy Builder — user-keyed (post bot-system removal)
// ============================================================================

export async function listStrategies() {
  const response = await fetchApi('/strategies');
  return response.data;
}

export async function createStrategy(strategy) {
  const response = await fetchApi('/strategies', {
    method: 'POST',
    body: JSON.stringify(strategy),
  });
  return response.data;
}

export async function getStrategy(strategyId) {
  const response = await fetchApi(`/strategies/${strategyId}`);
  return response.data;
}

export async function updateStrategy(strategyId, strategy) {
  const response = await fetchApi(`/strategies/${strategyId}`, {
    method: 'PUT',
    body: JSON.stringify(strategy),
  });
  return response.data;
}

export async function deleteStrategy(strategyId) {
  const response = await fetchApi(`/strategies/${strategyId}`, { method: 'DELETE' });
  return response.data;
}

export async function runStrategyBacktest(strategyId, config) {
  const response = await fetchApi(`/strategies/${strategyId}/backtest`, {
    method: 'POST',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function listStrategyBacktests(strategyId) {
  const response = await fetchApi(`/strategies/${strategyId}/backtests`);
  return response.data;
}

export async function getBacktest(backtestId) {
  const response = await fetchApi(`/backtests/${backtestId}`);
  return response.data;
}

// Legacy bot-keyed strategy fetch — kept ONLY so MarketplacePage's "your bots'
// strategies to publish" loader doesn't throw. Bot system is removed; this
// always returns null. MarketplacePage will be re-pointed at listStrategies()
// in its own audit pass.
export async function getBotStrategy() {
  return null;
}

export async function getStrategyTemplates() {
  const response = await fetchApi('/strategy-library/templates');
  return response.data;
}

export async function getConditionTypes() {
  const response = await fetchApi('/strategy-library/condition-types');
  return response.data;
}

export async function getActionTypes() {
  const response = await fetchApi('/strategy-library/action-types');
  return response.data;
}

// ============================================================================
// Alerts (Cap 6)
// ============================================================================

export async function getAlertConfigs() {
  const response = await fetchApi('/alerts/config');
  return response.data;
}

export async function createAlertConfig(config) {
  const response = await fetchApi('/alerts/config', {
    method: 'POST',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function updateAlertConfig(alertId, config) {
  const response = await fetchApi(`/alerts/config/${alertId}`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function deleteAlertConfig(alertId) {
  const response = await fetchApi(`/alerts/config/${alertId}`, { method: 'DELETE' });
  return response.data;
}

export async function getAlertHistory(days = 7, type = '', severity = '') {
  const params = new URLSearchParams({ days });
  if (type) params.append('type', type);
  if (severity) params.append('severity', severity);
  const response = await fetchApi(`/alerts/history?${params}`);
  return response.data;
}

// Notification bell — lightweight endpoint that returns ONLY the count of
// unread alerts in the last 7 days. Polled every ~30s by NotificationBell.jsx.
// Keep the payload tiny; the dropdown loads full history separately on open.
export async function getAlertUnreadCount() {
  return apiRequest('/account/alert-history/unread-count');
}

// Bell dropdown loads recent alerts with read state annotated.
export async function getAlertHistoryWithReadState({ sinceDays = 7 } = {}) {
  return apiRequest(`/account/alert-history?sinceDays=${sinceDays}`);
}

// "Mark all read" — passes no body so server defaults to "now" as the
// cutoff. After this fires, the unread count should drop to 0.
export async function markAlertsRead({ allBefore } = {}) {
  return apiRequest('/account/alert-history/mark-read', {
    method: 'POST',
    body: JSON.stringify(allBefore ? { allBefore } : {}),
  });
}

export async function testAlert(config) {
  const response = await fetchApi('/alerts/test', {
    method: 'POST',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function getLatestAlerts() {
  const response = await fetchApi('/alerts/stream');
  return response.data;
}

// ============================================================================
// Multi-Chain (Cap 7)
// ============================================================================

export async function getSupportedChains() {
  const response = await fetchApi('/chains');
  return response.data;
}

export async function getChain(chainId) {
  const response = await fetchApi(`/chains/${chainId}`);
  return response.data;
}

export async function getChainDexes(chainId) {
  const response = await fetchApi(`/chains/${chainId}/dexes`);
  return response.data;
}

export async function discoverToken(chainId, tokenAddress) {
  const response = await fetchApi('/tokens/discover', {
    method: 'POST',
    body: JSON.stringify({ chainId, tokenAddress }),
  });
  return response.data;
}

export async function getCrossChainPortfolio(walletAddress = '') {
  const qs = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
  const response = await fetchApi(`/portfolio/cross-chain${qs}`);
  return response.data;
}

export async function getChainHeatmap(walletAddress = '') {
  const qs = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
  const response = await fetchApi(`/portfolio/chain-heatmap${qs}`);
  return response.data;
}

export async function getGasPrices() {
  const response = await fetchApi('/chains/gas-prices');
  return response.data;
}

export async function getArbitrageOpportunities() {
  const response = await fetchApi('/chains/arbitrage');
  return response.data;
}

// ============================================================================
// Marketplace (Cap 8)
// ============================================================================

export async function getMarketplaceStrategies(filters = {}) {
  const params = new URLSearchParams(filters);
  const response = await fetchApi(`/marketplace/strategies?${params}`);
  return response.data;
}

export async function getMarketplaceStrategy(id) {
  const response = await fetchApi(`/marketplace/strategies/${id}`);
  return response.data;
}

export async function publishStrategy(strategy) {
  const response = await fetchApi('/marketplace/strategies/publish', {
    method: 'POST',
    body: JSON.stringify(strategy),
  });
  return response.data;
}

export async function unpublishStrategy(id) {
  const response = await fetchApi(`/marketplace/strategies/${id}`, { method: 'DELETE' });
  return response.data;
}

export async function deployMarketplaceStrategy(id, config) {
  const response = await fetchApi(`/marketplace/strategies/${id}/deploy`, {
    method: 'POST',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function getMarketplacePerformance(id) {
  const response = await fetchApi(`/marketplace/strategies/${id}/performance`);
  return response.data;
}

export async function addMarketplaceReview(id, review) {
  const response = await fetchApi(`/marketplace/strategies/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(review),
  });
  return response.data;
}

export async function getCreatorDashboard() {
  const response = await fetchApi('/creator/dashboard');
  return response.data;
}

export async function getCreatorEarnings() {
  const response = await fetchApi('/creator/earnings');
  return response.data;
}

// ============================================================================
// Referral
// ============================================================================

export async function getReferralStats() {
  const response = await fetchApi('/referral/stats');
  return response.data;
}

// ============================================================================
// Copy Trading — v2 (rebuilt on the new bot system)
// ============================================================================

export async function getCopyTradingLeaderboard() {
  const response = await fetchApi('/copy-trading/leaderboard');
  return response.data;
}

export async function publishBotForCopy(botType, botId, isPublic, displayName) {
  const response = await fetchApi('/copy-trading/publish', {
    method: 'POST',
    body: JSON.stringify({ botType, botId, public: isPublic, displayName }),
  });
  return response.data;
}

export async function followBot(botRef, params = {}) {
  // params for follow: { exchangeId, allocationUsd, maxDrawdownPct }
  // params for unfollow: just call with action: 'unfollow'
  const action = params.action || 'follow';
  const body = { botRef, action, ...params };
  const response = await fetchApi('/copy-trading/follow', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.data;
}

export async function getCopyTradingFollowing() {
  const response = await fetchApi('/copy-trading/following');
  return response.data;
}

export async function pauseCopyTrading(botRef, action = 'pause') {
  const response = await fetchApi('/copy-trading/pause', {
    method: 'POST',
    body: JSON.stringify({ botRef, action }),
  });
  return response.data;
}

export async function getCopyTradingMasterEarnings() {
  const response = await fetchApi('/copy-trading/master-earnings');
  return response.data;
}

export async function getMyPositions() {
  const response = await fetchApi('/copy-trading/my-positions');
  return response.data;
}

// ============================================================================
// Paper Trading — v2 (live strategy simulation, not historical backtest)
// ============================================================================

export async function getPaperSessions() {
  const response = await fetchApi('/paper-trading/sessions');
  return response.data;
}

export async function getPaperSession(sessionId) {
  const response = await fetchApi(`/paper-trading/sessions/${sessionId}`);
  return response.data;
}

export async function createPaperSession({ strategyId, exchange, symbol, timeframe, initialCapital }) {
  const response = await fetchApi('/paper-trading/sessions', {
    method: 'POST',
    body: JSON.stringify({ strategyId, exchange, symbol, timeframe, initialCapital }),
  });
  return response.data;
}

export async function pausePaperSession(sessionId) {
  const response = await fetchApi(`/paper-trading/sessions/${sessionId}/pause`, { method: 'POST' });
  return response.data;
}

export async function resumePaperSession(sessionId) {
  const response = await fetchApi(`/paper-trading/sessions/${sessionId}/resume`, { method: 'POST' });
  return response.data;
}

export async function stopPaperSession(sessionId) {
  const response = await fetchApi(`/paper-trading/sessions/${sessionId}/stop`, { method: 'POST' });
  return response.data;
}

export async function deletePaperSession(sessionId) {
  const response = await fetchApi(`/paper-trading/sessions/${sessionId}`, {
    method: 'DELETE',
  });
  return response.data;
}

// ============================================================================
// Admin Center
// ============================================================================

export async function getAdminSystemOverview() {
  const response = await fetchApi('/mgmt/system-overview');
  return response.data;
}

export async function getAdminConfig() {
  const response = await fetchApi('/mgmt/config');
  return response.data;
}

export async function getAdConfig() {
  const response = await fetchApi('/mgmt/ads');
  return response.data;
}

export async function saveAdConfig(config) {
  const response = await fetchApi('/mgmt/ads', {
    method: 'PUT',
    body: JSON.stringify(config),
  });
  return response.data;
}

// Public — no auth token required
export async function createAdCheckout(payload) {
  const res = await fetch(`${API_BASE_URL}/ads/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error?.message || 'Checkout failed');
  return json.data;
}

export async function getAdRequests(status = '') {
  const qs = status ? `?status=${status}` : '';
  const response = await fetchApi(`/mgmt/ads/requests${qs}`);
  return response.data;
}

export async function approveAdRequest(id, slotId) {
  const response = await fetchApi(`/mgmt/ads/requests/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ slotId }),
  });
  return response.data;
}

export async function rejectAdRequest(id, reason = '') {
  const response = await fetchApi(`/mgmt/ads/requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return response.data;
}

export async function deleteAdRequest(id) {
  const response = await fetchApi(`/mgmt/ads/requests/${id}/delete`, {
    method: 'POST',
  });
  return response.data;
}

// ─── User Management ──────────────────────────────────────────────────────────

export async function adminListUsers() {
  const response = await fetchApi('/mgmt/users');
  return response.data;
}

export async function adminResetPassword(userId, newPassword) {
  const response = await fetchApi(`/mgmt/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ newPassword }),
  });
  return response.data;
}

export async function adminChangeTier(userId, tier, cancelStripe = false) {
  const response = await fetchApi(`/mgmt/users/${userId}/tier`, {
    method: 'POST',
    body: JSON.stringify({ tier, cancelStripe }),
  });
  return response.data;
}

export async function adminDeleteUser(userId) {
  const response = await fetchApi(`/mgmt/users/${userId}`, { method: 'DELETE' });
  return response.data;
}

export async function adminSuspendUser(userId, suspended) {
  const response = await fetchApi(`/mgmt/users/${userId}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ suspended }),
  });
  return response.data;
}

export async function forgotPassword(email) {
  const response = await fetchApi('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return response.data;
}

export async function resetPassword(token, password, mfaCode) {
  // mfaCode is optional — only required when the account has MFA enabled.
  // The backend returns 401 with code='MFA_REQUIRED' when missing; callers
  // should retry with the user-entered code.
  const body = { token, password };
  if (mfaCode) body.mfaCode = mfaCode;
  const response = await fetchApi('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.data;
}

export async function uploadAdImage(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}/ads/upload-image`, { method: 'POST', body: form });
  const json = await res.json();
  if (!json.success) throw new Error(json.error?.message || 'Upload failed');
  return json.data.imageUrl;
}

export async function saveAdminConfig(key, value) {
  const response = await fetchApi('/mgmt/config', {
    method: 'POST',
    body: JSON.stringify({ key, value }),
  });
  return response.data;
}

export async function getAdminStorageStats() {
  const response = await fetchApi('/mgmt/storage-stats');
  return response.data;
}

export async function getAdminEventLogs(botId = '', limit = 50) {
  const params = new URLSearchParams({ limit });
  if (botId) params.append('botId', botId);
  const response = await fetchApi(`/mgmt/event-logs?${params}`);
  return response.data;
}

export async function purgeOldStorage(olderThanDays) {
  const response = await fetchApi('/mgmt/storage/purge-old', {
    method: 'DELETE',
    body: JSON.stringify({ olderThanDays }),
  });
  return response.data;
}

// ============================================================================
// AI Risk Analyst (Elite)
// ============================================================================

export async function aiRiskChat(message, conversationId) {
  const response = await fetchApi('/ai-risk-analyst/chat', {
    method: 'POST',
    body: JSON.stringify({ message, conversationId }),
  });
  return response.data;
}

export async function aiRiskAnalyze(days = 7) {
  const response = await fetchApi('/ai-risk-analyst/analyze', {
    method: 'POST',
    body: JSON.stringify({ days }),
  });
  return response.data;
}

export async function aiRiskHistory() {
  const response = await fetchApi('/ai-risk-analyst/history');
  return response.data;
}

export async function aiRiskLoadConversation(conversationId) {
  const response = await fetchApi(`/ai-risk-analyst/conversation/${conversationId}`);
  return response.data;
}

// ============================================================================
// Telegram Bot
// ============================================================================

export async function getTelegramStatus() {
  const response = await fetchApi('/telegram/status');
  return response.data;
}

export async function generateTelegramLink() {
  const response = await fetchApi('/telegram/generate-link', { method: 'POST' });
  return response.data;
}

export async function verifyTelegramLink(code) {
  const response = await fetchApi('/telegram/verify-link', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return response.data;
}

export async function disconnectTelegram() {
  const response = await fetchApi('/telegram/disconnect', { method: 'POST' });
  return response.data;
}

// ─── Telegram admin (webhook inspection + one-click registration) ───────────
export async function getTelegramWebhookStatus() {
  const r = await fetchApi('/telegram/admin/webhook');
  return r.data;
}
export async function registerTelegramWebhook() {
  const r = await fetchApi('/telegram/admin/webhook', { method: 'POST' });
  return r.data;
}

// ============================================================================
// Community Chat
// ============================================================================

export async function getChatRooms() {
  const response = await fetchApi('/chat/rooms');
  return response.data;
}

export async function getChatMessages(roomId, limit = 50) {
  const response = await fetchApi(`/chat/messages?roomId=${roomId}&limit=${limit}`);
  return response.data;
}

export async function sendChatMessage(roomId, content, replyTo, image) {
  const response = await fetchApi('/chat/send', {
    method: 'POST',
    body: JSON.stringify({ roomId, content, replyTo, image }),
  });
  return response.data;
}

export async function getRecentClosedTrades(limit = 20) {
  const response = await fetchApi(`/chat/recent-trades?limit=${limit}`);
  return response.data;
}

export async function shareTradeSnapshot(roomId, snapshotId, comment) {
  const response = await fetchApi('/chat/share-trade', {
    method: 'POST',
    body: JSON.stringify({ roomId, snapshotId, comment }),
  });
  return response.data;
}

export async function chatTyping(roomId) {
  const response = await fetchApi('/chat/typing', {
    method: 'POST',
    body: JSON.stringify({ roomId }),
  });
  return response.data;
}

export async function getTopCallsThisWeek(roomId) {
  const response = await fetchApi(`/calls/top-this-week?roomId=${encodeURIComponent(roomId)}`);
  return response.data;
}

export async function sendVoiceNote(roomId, audioDataUrl, durationMs) {
  const response = await fetchApi('/chat/send-voice', {
    method: 'POST',
    body: JSON.stringify({ roomId, audio: audioDataUrl, durationMs }),
  });
  return response.data;
}

export async function getChatVoice(audioId, roomId) {
  const response = await fetchApi(`/chat/voice?id=${encodeURIComponent(audioId)}&roomId=${encodeURIComponent(roomId)}`);
  return response.data;
}

export async function getAutoRenewSpender() {
  const response = await fetchApi('/channel-subs/auto-renew/spender');
  return response.data || response;
}

export async function enableAutoRenew({ subscriptionId, subscriberWallet, approvalTxHash, maxMonths, chain }) {
  const response = await fetchApi('/channel-subs/auto-renew/enable', {
    method: 'POST',
    body: JSON.stringify({ subscriptionId, subscriberWallet, approvalTxHash, maxMonths, chain }),
  });
  return response.data || response;
}

export async function disableAutoRenew(subscriptionId) {
  const response = await fetchApi('/channel-subs/auto-renew/disable', {
    method: 'POST',
    body: JSON.stringify({ subscriptionId }),
  });
  return response.data || response;
}

export async function getAdminChannelDetail(channelId) {
  const response = await fetchApi(`/paid-channels/admin/channel/${encodeURIComponent(channelId)}`);
  return response.data || response;
}

export async function getMyChannelEarnings() {
  const response = await fetchApi('/paid-channels/my-channel-earnings');
  return response.data || response;
}

export async function createTierUsdcCheckout({ tier, period, chain, subscriberWallet }) {
  const response = await fetchApi('/usdc-checkout/create', {
    method: 'POST',
    body: JSON.stringify({ tier, period, chain, subscriberWallet }),
  });
  return response.data || response;
}

export async function confirmTierUsdcCheckout({ checkoutId, txHash }) {
  const response = await fetchApi('/usdc-checkout/confirm', {
    method: 'POST',
    body: JSON.stringify({ checkoutId, txHash }),
  });
  return response.data || response;
}

export async function createTeamUsdcCheckout({ planId, billingCadence, teamName, chain, subscriberWallet }) {
  const response = await fetchApi('/team/usdc-checkout/create', {
    method: 'POST',
    body: JSON.stringify({ planId, billingCadence, teamName, chain, subscriberWallet }),
  });
  return response.data || response;
}

export async function confirmTeamUsdcCheckout({ checkoutId, txHash }) {
  const response = await fetchApi('/team/usdc-checkout/confirm', {
    method: 'POST',
    body: JSON.stringify({ checkoutId, txHash }),
  });
  return response.data || response;
}

export async function getTeamDashboard() {
  const response = await fetchApi('/team/dashboard');
  return response.data || response;
}

export async function getChatImage(imageId, roomId) {
  const response = await fetchApi(`/chat/image?id=${imageId}&roomId=${roomId}`);
  return response.data;
}

export async function getChatAlerts() {
  const response = await fetchApi('/chat/alerts');
  return response.data;
}

export async function acknowledgeChatAlert(alertId) {
  const response = await fetchApi('/chat/alerts/ack', {
    method: 'POST',
    body: JSON.stringify({ alertId }),
  });
  return response.data;
}

export async function getGuidelinesStatus() {
  const response = await fetchApi('/chat/guidelines-status');
  return response.data;
}

export async function acknowledgeGuidelines() {
  const response = await fetchApi('/chat/guidelines-ack', { method: 'POST', body: '{}' });
  return response.data;
}

export async function getBannedUsers() {
  const response = await fetchApi('/chat/banned-users');
  return response.data;
}

export async function requestUnban(reason) {
  const response = await fetchApi('/chat/unban-request', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return response.data;
}

// ─── Channel Discovery API ──────────────────────────────────────────────────

export async function discoverChannels(search, category) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (category) params.set('category', category);
  const response = await fetchApi(`/channels/discover?${params}`);
  return response.data;
}

export async function joinChannel(channelId) {
  const response = await fetchApi(`/channels/${channelId}/join`, { method: 'POST', body: '{}' });
  return response.data;
}

export async function leaveChannel(channelId) {
  const response = await fetchApi(`/channels/${channelId}/leave`, { method: 'POST', body: '{}' });
  return response.data;
}

export async function deleteChannel(channelId) {
  const response = await fetchApi(`/channels/${channelId}/settings`, {
    method: 'POST',
    body: JSON.stringify({ deleted: true }),
  });
  return response.data;
}

// ─── Tip Jar API ────────────────────────────────────────────────────────────

export async function createTip(recipientUserId, amount, chain, note, isPublic) {
  const response = await fetchApi('/tips/create', { method: 'POST', body: JSON.stringify({ recipientUserId, amount, chain, note, isPublic }) });
  return response.data;
}

export async function getUserTips(userId) {
  const response = await fetchApi(`/tips/user/${userId}`);
  return response.data;
}

// ─── Founders Program API ───────────────────────────────────────────────────

export async function getFoundersStatus() {
  const response = await fetchApi('/founders/status');
  return response.data;
}

export async function claimFounderSlot() {
  const response = await fetchApi('/founders/claim', { method: 'POST', body: '{}' });
  return response.data;
}

// ─── USDC Checkout API ──────────────────────────────────────────────────────

export async function createUsdcCheckout(tier, chain, period) {
  const response = await fetchApi('/usdc-checkout/create', { method: 'POST', body: JSON.stringify({ tier, chain, period }) });
  return response.data;
}

// ─── Wallet API ─────────────────────────────────────────────────────────────

export async function saveWallet(address, chain, provider) {
  const response = await fetchApi('/wallet/save', { method: 'POST', body: JSON.stringify({ address, chain, provider }) });
  return response.data;
}

export async function getMyWallets() {
  const response = await fetchApi('/wallet/mine');
  return response.data;
}

export async function verifyTransaction(txHash, chain, checkoutId, tipId) {
  const response = await fetchApi('/wallet/verify-tx', { method: 'POST', body: JSON.stringify({ txHash, chain, checkoutId, tipId }) });
  return response.data;
}

// ─── Payment Tracking API ────────────────────────────────────────────────────

export async function getPaymentTransactions() {
  const response = await fetchApi('/payments/transactions');
  return response.data;
}

// ─── Paid Channels API ──────────────────────────────────────────────────────

export async function getPaidChannelsStatus() {
  const response = await fetchApi('/paid-channels/status');
  return response.data;
}

// ─── Content Engine API ─────────────────────────────────────────────────────

export async function seedAllRooms() {
  const response = await fetchApi('/content/seed-all', { method: 'POST', body: '{}' });
  return response.data;
}

export async function generateRoomContent(roomId) {
  const response = await fetchApi(`/content/generate/${roomId}`, { method: 'POST', body: '{}' });
  return response.data;
}

// ─── Onboarding API ─────────────────────────────────────────────────────────

export async function getOnboardingStatus() {
  const response = await fetchApi('/onboarding/status');
  return response.data;
}

export async function completeOnboarding(interests) {
  const response = await fetchApi('/onboarding/complete', { method: 'POST', body: JSON.stringify({ interests }) });
  return response.data;
}

// ─── Project Verification API ────────────────────────────────────────────────

export async function applyForProjectVerification(data) {
  const response = await fetchApi('/projects/apply', { method: 'POST', body: JSON.stringify(data) });
  return response.data;
}

export async function getPendingProjects() {
  const response = await fetchApi('/projects/pending');
  return response.data;
}

export async function approveProject(projectId) {
  const response = await fetchApi('/projects/approve', { method: 'POST', body: JSON.stringify({ projectId }) });
  return response.data;
}

export async function rejectProject(projectId, reason) {
  const response = await fetchApi('/projects/reject', { method: 'POST', body: JSON.stringify({ projectId, reason }) });
  return response.data;
}

export async function deleteProject(projectId) {
  const response = await fetchApi('/projects/delete', { method: 'POST', body: JSON.stringify({ projectId }) });
  return response.data;
}

export async function getVerifiedProjects() {
  const response = await fetchApi('/projects/verified');
  return response.data;
}

// ─── Room Summary API ────────────────────────────────────────────────────────

export async function getRoomSummary(roomId) {
  const response = await fetchApi(`/summary/room/${roomId}`);
  return response.data;
}

// ─── Polls & Predictions API ─────────────────────────────────────────────────

export async function createPoll(roomId, question, options) {
  const response = await fetchApi('/polls/create', { method: 'POST', body: JSON.stringify({ roomId, question, options }) });
  return response.data;
}

export async function votePoll(pollId, optionIndex) {
  const response = await fetchApi('/polls/vote', { method: 'POST', body: JSON.stringify({ pollId, optionIndex }) });
  return response.data;
}

export async function resolvePoll(pollId, outcomeIndex) {
  const response = await fetchApi('/polls/resolve', { method: 'POST', body: JSON.stringify({ pollId, outcomeIndex }) });
  return response.data;
}

export async function createPrediction(roomId, asset, predictionType, targetPrice, resolveAt) {
  const response = await fetchApi('/predictions/create', { method: 'POST', body: JSON.stringify({ roomId, asset, predictionType, targetPrice, resolveAt }) });
  return response.data;
}

export async function votePrediction(predictionId, vote) {
  const response = await fetchApi('/predictions/vote', { method: 'POST', body: JSON.stringify({ predictionId, vote }) });
  return response.data;
}

export async function deletePollOrPrediction(pollId, predictionId) {
  const response = await fetchApi('/polls/delete', { method: 'POST', body: JSON.stringify({ pollId, predictionId }) });
  return response.data;
}

export async function getRoomPolls(roomId) {
  const response = await fetchApi(`/polls/room/${roomId}`);
  return response.data;
}

// ─── Account API (Phase 2 settings) ─────────────────────────────────────────

export async function getAccountPrivacy() {
  const response = await fetchApi('/account/privacy');
  return response.data;
}

export async function updateAccountPrivacy(updates) {
  const response = await fetchApi('/account/privacy', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return response.data;
}

export async function getAccountLoginHistory(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.category)   qs.set('category',  filters.category);
  if (filters.type)       qs.set('type',      filters.type);
  if (filters.ok != null) qs.set('ok',        String(filters.ok));
  if (filters.sinceHours) qs.set('sinceHours', String(filters.sinceHours));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await fetchApi(`/account/login-history${suffix}`);
  return response.data;
}

// CSV export — returns a Blob. Backend sets Content-Disposition for download.
export async function exportLoginHistoryCsv() {
  const url = `${API_BASE_URL}/account/login-history/export.csv`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  return res.blob();
}

// Data export — returns a Blob with the JSON dump. The endpoint sets
// Content-Disposition so the browser will download it. We use raw fetch
// here rather than fetchApi because we want the Blob, not parsed JSON.
export async function exportAccountData() {
  const url = `${API_BASE_URL}/account/export`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  return blob;
}

export async function requestAccountDeletion(confirm, reason) {
  const response = await fetchApi('/account/delete', {
    method: 'POST',
    body: JSON.stringify({ confirm, reason }),
  });
  return response.data;
}

export async function cancelAccountDeletion() {
  const response = await fetchApi('/account/cancel-delete', { method: 'POST' });
  return response.data;
}

export async function getAccountDeletionStatus() {
  const response = await fetchApi('/account/deletion-status');
  return response.data;
}

// ─── Active Sessions (Phase 2.5) ────────────────────────────────────────────

export async function listActiveSessions() {
  const r = await fetchApi('/account/sessions');
  return r.data;
}

export async function revokeSession(jti) {
  const r = await fetchApi(`/account/sessions/${encodeURIComponent(jti)}/revoke`, { method: 'POST' });
  return r.data;
}

export async function revokeAllOtherSessions() {
  const r = await fetchApi('/account/sessions/revoke-all-others', { method: 'POST' });
  return r.data;
}

// ─── Trading Defaults (per-user) ────────────────────────────────────────────
// Replaces the old broken `saveCloudSetting(SLIPPAGE_PCT…)` pattern which
// hit /api/mgmt/config and was rejected by the SAFE_CONFIG_KEYS whitelist.
// These are real per-user defaults persisted to ADLS and seed new bot
// creation across Smart Trade / Algo / DEX swap.

export async function getTradingDefaults() {
  const r = await fetchApi('/account/trading-defaults');
  return r.data;
}

export async function saveTradingDefaults(updates) {
  const r = await fetchApi('/account/trading-defaults', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return r.data;
}

export async function getRoutingStatus() {
  const r = await fetchApi('/account/trading-defaults/routing-status');
  return r.data;
}

// ─── Risk Rules (per-user, real enforcement) ────────────────────────────────
// Replaces the old broken pattern (saveCloudSetting → /api/mgmt/config → 403).

export async function getRiskRules() {
  const r = await fetchApi('/account/risk-rules');
  return r.data;
}

export async function saveRiskRules(updates) {
  const r = await fetchApi('/account/risk-rules', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return r.data;
}

export async function getRiskStatus() {
  const r = await fetchApi('/account/risk-rules/status');
  return r.data;
}

export async function getRiskPreview() {
  const r = await fetchApi('/account/risk-rules/preview');
  return r.data;
}

export async function applyRiskPreset(preset) {
  const r = await fetchApi('/account/risk-rules/apply-preset', {
    method: 'POST',
    body: JSON.stringify({ preset }),
  });
  return r.data;
}

// ─── Account Wallets + Chains + RPC health (per-user) ──────────────────────
// Replaces the broken old pattern: wallet stored in localStorage, networks
// list hardcoded in UI, RPC override save → 403.

export async function listMyWallets() {
  const r = await fetchApi('/account/wallets');
  return r.data;
}
export async function addMyWallet({ address, chain, label }) {
  const r = await fetchApi('/account/wallets', {
    method: 'POST',
    body: JSON.stringify({ address, chain, label }),
  });
  return r.data;
}
export async function updateMyWalletLabel(id, label) {
  const r = await fetchApi(`/account/wallets/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ label }),
  });
  return r.data;
}
export async function deleteMyWallet(id) {
  const r = await fetchApi(`/account/wallets/${id}`, { method: 'DELETE' });
  return r.data;
}
export async function getMySupportedChains() {
  const r = await fetchApi('/account/chains');
  return r.data;
}
export async function getChainsHealth() {
  const r = await fetchApi('/account/chains/health');
  return r.data;
}
export async function setPayoutWallet(walletId) {
  const r = await fetchApi('/account/wallets/payout', {
    method: 'PUT',
    body: JSON.stringify({ walletId }),
  });
  return r.data;
}
export async function getPayoutHistory() {
  const r = await fetchApi('/account/payouts/history');
  return r.data;
}
export async function clearExpiredPayouts() {
  const r = await fetchApi('/account/payouts/expired', { method: 'DELETE' });
  return r.data;
}

// ─── Subscription extras (billing context + usage) ──────────────────────────
export async function getBillingContext() {
  const r = await fetchApi('/subscriptions/billing-context');
  return r.data;
}
export async function getAccountUsage() {
  const r = await fetchApi('/account/usage');
  return r.data;
}

// ─── Tax Export ─────────────────────────────────────────────────────────────
export async function getTaxExportPreview(startDate, endDate) {
  const qs = new URLSearchParams();
  if (startDate) qs.set('startDate', startDate);
  if (endDate)   qs.set('endDate', endDate);
  const r = await fetchApi(`/tax-export/preview${qs.toString() ? '?' + qs.toString() : ''}`);
  return r.data;
}
export async function getTaxExportHistory() {
  const r = await fetchApi('/tax-export/history');
  return r.data;
}

export async function testExchangeConnection(exchangeId) {
  const r = await fetchApi(`/exchange/test/${exchangeId}`, { method: 'POST' });
  return r.data;
}

export async function emergencyHalt(reason) {
  const r = await fetchApi('/account/risk-rules/emergency-halt', {
    method: 'POST',
    body: JSON.stringify({ reason: reason || 'manual' }),
  });
  return r.data;
}

// ─── Alert Channels (per-user, real delivery) ───────────────────────────────
// Replaces old broken pattern (saveCloudSetting → /api/mgmt/config → 403
// + global shared alert configs visible across users).

export async function getAlertChannels() {
  const r = await fetchApi('/account/alert-channels');
  return r.data;
}

export async function saveAlertChannels(updates) {
  const r = await fetchApi('/account/alert-channels', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return r.data;
}

export async function testAlertChannels(severity = 'info') {
  const r = await fetchApi('/account/alert-channels/test', {
    method: 'POST',
    body: JSON.stringify({ severity }),
  });
  return r.data;
}

export async function getMyAlertHistory(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.severity) qs.set('severity', filters.severity);
  if (filters.type)     qs.set('type', filters.type);
  if (filters.sinceDays) qs.set('sinceDays', String(filters.sinceDays));
  const r = await fetchApi(`/account/alert-history${qs.toString() ? '?' + qs.toString() : ''}`);
  return r.data;
}

// ─── API Keys (Phase 4) ─────────────────────────────────────────────────────

export async function listApiKeys() {
  const response = await fetchApi('/account/api-keys');
  return response.data;
}

export async function createApiKey(name, scopes, expiresInDays) {
  // expiresInDays: 7|30|90|365 or null (never expires)
  const response = await fetchApi('/account/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name, scopes, expiresInDays: expiresInDays ?? null }),
  });
  return response.data;
}

export async function revokeApiKey(id) {
  const response = await fetchApi(`/account/api-keys/${id}`, { method: 'DELETE' });
  return response.data;
}

// Test a specific raw API key by calling the test endpoint with it.
// Bypasses fetchApi's default JWT header — we want THIS key, not the user's
// session, to be the auth method under test.
export async function testApiKey(rawKey) {
  const url = `${API_BASE_URL}/account/api-keys/test`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'X-API-Key': rawKey },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error?.message || `Test failed (HTTP ${res.status})`);
  }
  return data?.data || data;
}

// ─── Follow API ─────────────────────────────────────────────────────────────

export async function followUser(targetUserId) {
  const response = await fetchApi('/follow', { method: 'POST', body: JSON.stringify({ targetUserId }) });
  return response.data;
}

export async function unfollowUser(targetUserId) {
  const response = await fetchApi('/unfollow', { method: 'POST', body: JSON.stringify({ targetUserId }) });
  return response.data;
}

export async function checkFollowing(userId) {
  const response = await fetchApi(`/follow/check/${userId}`);
  return response.data;
}

export async function getFollowStats(userId) {
  const response = await fetchApi(`/follow/stats/${userId}`);
  return response.data;
}

export async function getLeaderboard(type) {
  const response = await fetchApi(`/leaderboards/${type}`);
  return response.data;
}

// ─── Call Tracking API ───────────────────────────────────────────────────────

export async function getMyCalls() {
  const response = await fetchApi('/calls/mine');
  return response.data;
}

export async function getUserCalls(userId) {
  const response = await fetchApi(`/calls/user/${userId}`);
  return response.data;
}

export async function getCallsLeaderboard() {
  const response = await fetchApi('/calls/leaderboard');
  return response.data;
}

// ─── Notifications API ───────────────────────────────────────────────────────

export async function getChatNotifications() {
  const response = await fetchApi('/chat/notifications');
  return response.data;
}

export async function markNotificationsRead(notificationId, all = false) {
  const response = await fetchApi('/chat/notifications/read', {
    method: 'POST',
    body: JSON.stringify({ notificationId, all }),
  });
  return response.data;
}

// ─── Streak API ──────────────────────────────────────────────────────────────

export async function getMyStreak() {
  const response = await fetchApi('/streak/mine');
  return response.data;
}

export async function getUserStreak(userId) {
  const response = await fetchApi(`/streak/user/${userId}`);
  return response.data;
}

export async function streakCheckin() {
  const response = await fetchApi('/streak/checkin', { method: 'POST', body: '{}' });
  return response.data;
}

export async function markChatRead(roomId) {
  const response = await fetchApi('/chat/mark-read', {
    method: 'POST',
    body: JSON.stringify({ roomId }),
  });
  return response.data;
}

export async function moderateChat(action, params) {
  const response = await fetchApi('/chat/moderate', {
    method: 'POST',
    body: JSON.stringify({ action, ...params }),
  });
  return response.data;
}

export async function assignChatRole(targetUserId, role) {
  const response = await fetchApi('/chat/assign-role', {
    method: 'POST',
    body: JSON.stringify({ targetUserId, role }),
  });
  return response.data;
}

export async function getChatRolesList() {
  const response = await fetchApi('/chat/roles-list');
  return response.data;
}

// ============================================================================
// Community Profiles & Achievements
// ============================================================================

export async function getCommunityProfile(userId) {
  const response = await fetchApi(`/community/profile/${userId}`);
  return response.data;
}

export async function updateCommunityProfile(bio, twitter, website, avatar) {
  const body = { bio, twitter, website };
  if (avatar !== undefined) body.avatar = avatar;
  const response = await fetchApi('/community/profile', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.data;
}

export async function getCommunityLeaderboard() {
  const response = await fetchApi('/community/leaderboard');
  return response.data;
}

export async function getCommunityAchievements(userId) {
  const response = await fetchApi(`/community/achievements/${userId}`);
  return response.data;
}

export async function chatHeartbeat(roomId) {
  const response = await fetchApi('/chat/heartbeat', { method: 'POST', body: JSON.stringify({ roomId }) });
  return response.data;
}

export async function getChatOnlineUsers(roomId) {
  const response = await fetchApi(`/chat/online?roomId=${roomId}`);
  return response.data;
}

export async function reactToMessage(messageId, roomId, reaction) {
  const response = await fetchApi('/chat/react', {
    method: 'POST',
    body: JSON.stringify({ messageId, roomId, reaction }),
  });
  return response.data;
}

export async function getChatReactions(roomId) {
  const response = await fetchApi(`/chat/reactions?roomId=${roomId}`);
  return response.data;
}

export async function createChatChannel(name, description, icon, category) {
  const response = await fetchApi('/chat/create-channel', {
    method: 'POST',
    body: JSON.stringify({ name, description, icon, category }),
  });
  return response.data;
}

// ============================================================================
// Smart Order Routing
// ============================================================================

export async function getRoutingQuote(network, fromToken, toToken, amount, fromDecimals, toDecimals) {
  const response = await fetchApi('/routing/quote', {
    method: 'POST',
    body: JSON.stringify({ network, fromToken, toToken, amount, fromDecimals, toDecimals }),
  });
  return response.data;
}

export async function getRoutingAggregators() {
  const response = await fetchApi('/routing/aggregators');
  return response.data;
}

// ============================================================================
// Tokenomics Advisor (Elite)
// ============================================================================

export async function analyzeTokenomicsText(text, tokenName, tokenAddress) {
  const response = await fetchApi('/tokenomics-advisor/analyze-text', {
    method: 'POST',
    body: JSON.stringify({ text, tokenName, tokenAddress }),
  });
  return response.data;
}

export async function analyzeTokenomicsPdf(file, tokenName, tokenAddress) {
  const formData = new FormData();
  formData.append('pdf', file);
  formData.append('tokenName', tokenName);
  formData.append('tokenAddress', tokenAddress);

  const url = `${API_BASE_URL}/tokenomics-advisor/analyze-pdf`;
  const headers = {};
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

  const res = await fetch(url, { method: 'POST', headers, body: formData });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message || 'Upload failed');
  return data.data;
}

export async function iterateTokenomics(analysisId, question) {
  const response = await fetchApi('/tokenomics-advisor/iterate', {
    method: 'POST',
    body: JSON.stringify({ analysisId, question }),
  });
  return response.data;
}

export async function applyTokenomicsStrategy(analysisId, network) {
  const response = await fetchApi('/tokenomics-advisor/apply-strategy', {
    method: 'POST',
    body: JSON.stringify({ analysisId, network }),
  });
  return response.data;
}

export async function listTokenomicsAnalyses() {
  const response = await fetchApi('/tokenomics-advisor/analyses');
  return response.data;
}

export async function getTokenomicsAnalysis(analysisId) {
  const response = await fetchApi(`/tokenomics-advisor/analyses/${analysisId}`);
  return response.data;
}

// ============================================================================
// Competitor Tracker (Elite)
// ============================================================================

export async function addCompetitor(tokenAddress, tokenName, network) {
  const response = await fetchApi('/competitor-tracker/competitors', {
    method: 'POST',
    body: JSON.stringify({ tokenAddress, tokenName, network }),
  });
  return response.data;
}

export async function listCompetitors() {
  const response = await fetchApi('/competitor-tracker/competitors');
  return response.data;
}

export async function removeCompetitor(tokenAddress) {
  const response = await fetchApi(`/competitor-tracker/competitors/${tokenAddress}`, { method: 'DELETE' });
  return response.data;
}

export async function analyzeCompetitor(tokenAddress, network = 'ethereum') {
  const response = await fetchApi('/competitor-tracker/analyze', {
    method: 'POST',
    body: JSON.stringify({ tokenAddress, network }),
  });
  return response.data;
}

export async function compareCompetitors(ownToken, competitorAddresses = []) {
  const response = await fetchApi('/competitor-tracker/compare', {
    method: 'POST',
    body: JSON.stringify({ ownToken, competitorAddresses }),
  });
  return response.data;
}

// ============================================================================
// Volume Health Widget (Elite)
// ============================================================================

export async function registerHealthToken(tokenAddress, tokenName, network) {
  const response = await fetchApi('/volume-health/register', {
    method: 'POST',
    body: JSON.stringify({ tokenAddress, tokenName, network }),
  });
  return response.data;
}

export async function listHealthTokens() {
  const response = await fetchApi('/volume-health/tokens');
  return response.data;
}

export async function deleteHealthToken(tokenAddress) {
  const response = await fetchApi(`/volume-health/tokens/${tokenAddress}`, {
    method: 'DELETE',
  });
  return response.data;
}

export async function getHealthScore(tokenAddress, chain = 'ethereum', opts = {}) {
  const qs = opts.forceRefresh ? '?refresh=1' : '';
  const url = `${API_BASE_URL}/volume-health/score/${chain}/${tokenAddress}${qs}`;
  const res = await fetch(url);
  return res.json();
}

// ============================================================================
// Launch Roadmap Planner (Elite) — replaces the wash-trading "Campaign
// Scheduler" with a real milestone tracker.
// ============================================================================

export async function createRoadmap(config) {
  const response = await fetchApi('/launch-roadmap/roadmaps', {
    method: 'POST',
    body: JSON.stringify(config),
  });
  return response.data;
}

export async function listRoadmaps() {
  const response = await fetchApi('/launch-roadmap/roadmaps');
  return response.data;
}

export async function updateRoadmap(roadmapId, updates) {
  const response = await fetchApi(`/launch-roadmap/roadmaps/${roadmapId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return response.data;
}

export async function deleteRoadmap(roadmapId) {
  const response = await fetchApi(`/launch-roadmap/roadmaps/${roadmapId}`, {
    method: 'DELETE',
  });
  return response.data;
}

export async function previewRoadmap(phases, startDate) {
  const response = await fetchApi('/launch-roadmap/preview', {
    method: 'POST',
    body: JSON.stringify({ phases, startDate }),
  });
  return response.data;
}

export async function toggleRoadmapMilestone(roadmapId, phaseIndex, milestoneId) {
  const response = await fetchApi(`/launch-roadmap/roadmaps/${roadmapId}/milestone-toggle`, {
    method: 'POST',
    body: JSON.stringify({ phaseIndex, milestoneId }),
  });
  return response.data;
}

// ============================================================================
// Utility
// ============================================================================

// Re-export currency-aware formatter from displayPrefs so this widely-used
// helper respects the user's Settings → Language & Region preference. The
// signature stays backward-compatible (callers pass a USD value).
import { formatUsd as _formatUsdAware } from './displayPrefs';
export function formatUsd(amount) {
  return _formatUsdAware(amount, 2);
}

export function formatPercent(value) {
  if (value === null || value === undefined) return '-';
  return `${parseFloat(value).toFixed(1)}%`;
}

export function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(value);
}

export function shortenAddress(address) {
  if (!address) return '-';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function shortenTxHash(hash) {
  if (!hash) return '-';
  return `${hash.slice(0, 10)}...${hash.slice(-6)}`;
}

export function getExplorerUrl(network, txHash) {
  const explorers = {
    ethereum_mainnet: 'https://etherscan.io/tx/',
    polygon_mainnet: 'https://polygonscan.com/tx/',
    arbitrum_mainnet: 'https://arbiscan.io/tx/',
    base_mainnet: 'https://basescan.org/tx/',
  };
  return `${explorers[network] || explorers.ethereum_mainnet}${txHash}`;
}

// ─── CEX Exchange API ──────────────────────────────────────────────────────

export async function connectExchange(exchangeId, apiKey, secret, passphrase = null) {
  return apiRequest('/exchange/connect', { method: 'POST', body: JSON.stringify({ exchangeId, apiKey, secret, passphrase }) });
}

export async function disconnectExchange(exchangeId) {
  return apiRequest('/exchange/disconnect', { method: 'DELETE', body: JSON.stringify({ exchangeId }) });
}

export async function getExchangeConnections() {
  return apiRequest('/exchange/connections');
}

export async function getExchangeBalance(exchangeId) {
  return apiRequest(`/exchange/balance/${exchangeId}`);
}

export async function getAllExchangeBalances() {
  return apiRequest('/exchange/balance-all');
}

export async function placeExchangeOrder(exchangeId, symbol, side, type, amount, price = null, params = {}, costQuote = null) {
  return apiRequest('/exchange/order', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, side, type, amount, price, params, costQuote }) });
}

export async function cancelExchangeOrder(exchangeId, orderId, symbol) {
  return apiRequest('/exchange/order', { method: 'DELETE', body: JSON.stringify({ exchangeId, orderId, symbol }) });
}

export async function getExchangeOpenOrders(exchangeId, symbol = null) {
  const params = symbol ? `?symbol=${encodeURIComponent(symbol)}` : '';
  return apiRequest(`/exchange/orders/${exchangeId}${params}`);
}

export async function getExchangeTradeHistory(exchangeId, symbol = null, limit = 50) {
  const params = new URLSearchParams();
  if (symbol) params.set('symbol', symbol);
  if (limit) params.set('limit', limit);
  return apiRequest(`/exchange/history/${exchangeId}?${params}`);
}

export async function getExchangeMarkets(exchangeId) {
  return apiRequest(`/exchange/markets/${exchangeId}`);
}

// Public symbol list — does NOT require the user to have connected the exchange.
// Backed by CCXT loadMarkets() with no API keys, 1-hour server cache.
// Use this when you just need a real per-exchange pair list (Quant Lab, Backtest, etc.).
export async function getExchangePublicMarkets(exchangeId) {
  return apiRequest(`/exchange/public-markets/${exchangeId}`);
}

// Single source of truth for which exchanges the platform supports. Every
// dropdown that shows "exchanges" should pull from here, with capability flags
// (backtestSupported, futuresSupported, minTier) so each feature can filter
// appropriately. Keeps Elite menus in sync with the 16 exchanges we market.
export async function getSupportedExchanges() {
  return apiRequest(`/exchange/supported`);
}

export async function getExchangeTicker(exchangeId, symbol) {
  return apiRequest(`/exchange/ticker?exchange=${exchangeId}&symbol=${encodeURIComponent(symbol)}`);
}

// ─── Futures Trading API ───────────────────────────────────────────────────

export async function getFuturesExchanges() {
  return apiRequest('/futures/exchanges');
}

export async function getFuturesPositions(exchangeId) {
  return apiRequest(`/futures/positions/${exchangeId}`);
}

export async function getFuturesBalance(exchangeId) {
  return apiRequest(`/futures/balance/${exchangeId}`);
}

export async function getFuturesMarkets(exchangeId) {
  return apiRequest(`/futures/markets/${exchangeId}`);
}

export async function openFuturesPosition(exchangeId, symbol, side, amount, leverage, marginMode, stopLoss = null, takeProfit = null) {
  return apiRequest('/futures/open', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, side, amount, leverage, marginMode, stopLoss, takeProfit }) });
}

export async function closeFuturesPosition(exchangeId, symbol, side, amount) {
  return apiRequest('/futures/close', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, side, amount }) });
}

export async function setFuturesLeverage(exchangeId, symbol, leverage) {
  return apiRequest('/futures/leverage', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, leverage }) });
}

export async function setFuturesMarginMode(exchangeId, symbol, marginMode) {
  return apiRequest('/futures/margin-mode', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, marginMode }) });
}

// ─── DEX Swap API ──────────────────────────────────────────────────────────

export async function getSwapQuote(chain, fromToken, toToken, amount, fromDecimals, toDecimals, slippage) {
  return apiRequest('/swap/quote', { method: 'POST', body: JSON.stringify({ chain, fromToken, toToken, amount, fromDecimals, toDecimals, slippage }) });
}

// quoteTimestamp + txHash are required for the new wallet-signing flow — the
// backend validates the txHash is a real 0x... 64-char hash before recording
// the swap and queueing it for on-chain confirmation by swapPendingChecker.
export async function executeSwap(chain, fromToken, toToken, fromSymbol, toSymbol, amount, slippage, route, quoteTimestamp, txHash, expectedAmountOut) {
  return apiRequest('/swap/execute', {
    method: 'POST',
    body: JSON.stringify({ chain, fromToken, toToken, fromSymbol, toSymbol, amount, slippage, route, quoteTimestamp, txHash, expectedAmountOut }),
  });
}

export async function getSwapHistory() {
  return apiRequest('/swap/history');
}

// Backfill DEX swap amountOut values for historical records. Reads each swap's
// on-chain receipt + parses Transfer event logs to extract the actual received
// amount, then patches the swap-history record. Safe to call repeatedly —
// only swaps with null/zero amountOut are touched. Returns counts of what was
// scanned / patched / skipped so the UI can show what happened.
export async function backfillSwapAmounts() {
  return apiRequest('/swap/backfill-amounts', { method: 'POST' });
}

export async function getTokenInfo(chain, address) {
  return apiRequest(`/swap/token/${chain}/${address}`);
}

// DEX Limit Orders — trigger-based engine, all 7 chains supported.
// Worker monitors price every 30s; when threshold crossed, order flips to
// 'ready_to_execute' and user gets a notification. Then user clicks Execute
// in the Limit Orders tab — frontend builds tx via Paraswap, wallet signs,
// frontend posts txHash to markLimitOrderFilled.

export async function createLimitOrder(chain, fromToken, toToken, fromSymbol, toSymbol, fromAmount, targetPrice, direction, expireHours, walletAddress, fromDecimals, toDecimals) {
  return apiRequest('/dex-limit/orders', { method: 'POST', body: JSON.stringify({
    chain, fromToken, toToken, fromSymbol, toSymbol, fromAmount, targetPrice, direction, expireHours,
    walletAddress, fromDecimals, toDecimals,
  }) });
}

export async function getLimitOrders() {
  return apiRequest('/dex-limit/orders');
}

export async function cancelLimitOrder(orderId) {
  return apiRequest(`/dex-limit/orders/${orderId}`, { method: 'DELETE' });
}

export async function markLimitOrderFilled(orderId, payload) {
  return apiRequest(`/dex-limit/orders/${orderId}/filled`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function createDcaSchedule(chain, fromToken, toToken, fromSymbol, toSymbol, amountPerSwap, frequency, totalSwaps, slippage) {
  return apiRequest('/swap/dca', { method: 'POST', body: JSON.stringify({ chain, fromToken, toToken, fromSymbol, toSymbol, amountPerSwap, frequency, totalSwaps, slippage }) });
}

export async function getDcaSchedules() {
  return apiRequest('/swap/dca');
}

export async function cancelDcaSchedule(dcaId) {
  return apiRequest(`/swap/dca/${dcaId}`, { method: 'DELETE' });
}

export async function getTrendingTokens(chain) {
  return apiRequest(`/swap/trending?chain=${chain}`);
}

export async function analyzeMev(txHash, chain) {
  return apiRequest('/swap/analyze-mev', { method: 'POST', body: JSON.stringify({ txHash, chain }) });
}

// ─── Tax Strategy (Vertical 4 — harvest + what-if) ─────────────────────────

export async function getTaxLots() {
  return apiRequest('/tax-strategy/lots');
}
export async function getTaxHarvest({ minHarvestUsd, taxRatePct } = {}) {
  const qs = new URLSearchParams();
  if (Number.isFinite(minHarvestUsd)) qs.set('minHarvestUsd', String(minHarvestUsd));
  if (Number.isFinite(taxRatePct))    qs.set('taxRatePct', String(taxRatePct));
  const tail = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest(`/tax-strategy/harvest${tail}`);
}
export async function postWhatIf({ currentWeights, proposedWeights, lookbackDays = 90 }) {
  return apiRequest('/tax-strategy/what-if', {
    method: 'POST',
    body: JSON.stringify({ currentWeights, proposedWeights, lookbackDays }),
  });
}
export async function getTaxReplacementMap() {
  return apiRequest('/tax-strategy/replacement-map');
}

// ─── Tokenomics-as-a-Service B2B (Vertical 5) ──────────────────────────────

export async function listTokenomicsProjects() {
  return apiRequest('/tokenomics-projects');
}
export async function createTokenomicsProject({ name, plan = 'starter', tokens = [] }) {
  return apiRequest('/tokenomics-projects', { method: 'POST', body: JSON.stringify({ name, plan, tokens }) });
}
export async function getTokenomicsProject(projectId) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}`);
}
export async function updateTokenomicsProject(projectId, patch) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}
export async function deleteTokenomicsProject(projectId) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}`, { method: 'DELETE' });
}
export async function addTokenomicsToken(projectId, tokenSpec) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/tokens`, { method: 'POST', body: JSON.stringify(tokenSpec) });
}
export async function removeTokenomicsToken(projectId, { network, contractAddress }) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/tokens`, { method: 'DELETE', body: JSON.stringify({ network, contractAddress }) });
}
export async function addTokenomicsUnlock(projectId, unlock) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/unlocks`, { method: 'POST', body: JSON.stringify(unlock) });
}
export async function removeTokenomicsUnlock(projectId, unlockId) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/unlocks/${encodeURIComponent(unlockId)}`, { method: 'DELETE' });
}
export async function getTokenomicsHolders(projectId, { network, contractAddress }) {
  const qs = new URLSearchParams({ network, contractAddress });
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/holders?${qs.toString()}`);
}
export async function simulateTokenomicsUnlock(projectId, payload) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/simulate`, { method: 'POST', body: JSON.stringify(payload) });
}
export async function issueTokenomicsApiKey(projectId, { label = 'default' } = {}) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/api-keys`, { method: 'POST', body: JSON.stringify({ label }) });
}
export async function revokeTokenomicsApiKey(projectId, keyHash) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/api-keys/${encodeURIComponent(keyHash)}`, { method: 'DELETE' });
}
// BYO explorer API key per chain — used to unlock live holder data.
export async function setTokenomicsExplorerKey(projectId, chain, apiKey) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/explorer-keys/${encodeURIComponent(chain)}`, { method: 'PUT', body: JSON.stringify({ apiKey }) });
}
export async function removeTokenomicsExplorerKey(projectId, chain) {
  return apiRequest(`/tokenomics-projects/by-id/${encodeURIComponent(projectId)}/explorer-keys/${encodeURIComponent(chain)}`, { method: 'DELETE' });
}
export async function getTokenomicsPublicReport(publicSlug) {
  // Public endpoint — no auth header sent. Use raw fetch to avoid auth-redirect logic.
  const url = `${API_BASE_URL}/tokenomics/r/${encodeURIComponent(publicSlug)}`;
  const r = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
  return r.json();
}

// ─── Google OAuth (Prompt 38 — additive, not replacing email/password) ─────

/**
 * Ask the backend for the Google authorize URL, then redirect the browser there.
 * The backend signs an opaque `state` for CSRF and embeds the post-login redirect
 * path. After the user approves, Google redirects to /api/auth/google/callback,
 * which 302s the browser to /auth/callback?token=... in the frontend.
 */
export async function startGoogleSignIn(redirectAfter = '/') {
  const url = `${API_BASE_URL}/auth/google/initiate?redirect=${encodeURIComponent(redirectAfter)}`;
  const r = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
  const json = await r.json().catch(() => null);
  if (!json?.success || !json.data?.authUrl) {
    throw new Error(json?.error?.message || 'Google sign-in is not configured');
  }
  window.location.href = json.data.authUrl;
}

// ─── Token Universe Scanner (Vertical 7) ───────────────────────────────────

export async function listScans() {
  return apiRequest('/scanner/scans');
}
export async function createScan(payload) {
  return apiRequest('/scanner/scans', { method: 'POST', body: JSON.stringify(payload) });
}
export async function getScan(scanId) {
  return apiRequest(`/scanner/scans/by-id/${encodeURIComponent(scanId)}`);
}
export async function updateScan(scanId, patch) {
  return apiRequest(`/scanner/scans/by-id/${encodeURIComponent(scanId)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}
export async function deleteScan(scanId) {
  return apiRequest(`/scanner/scans/by-id/${encodeURIComponent(scanId)}`, { method: 'DELETE' });
}
export async function runScanNow(scanId) {
  return apiRequest(`/scanner/scans/by-id/${encodeURIComponent(scanId)}/run`, { method: 'POST', body: JSON.stringify({}) });
}
export async function listScanRuns(scanId) {
  return apiRequest(`/scanner/scans/by-id/${encodeURIComponent(scanId)}/runs`);
}
export async function getLatestRun(scanId) {
  return apiRequest(`/scanner/scans/by-id/${encodeURIComponent(scanId)}/latest-run`);
}
export async function getScanRun(scanId, runId) {
  return apiRequest(`/scanner/scans/by-id/${encodeURIComponent(scanId)}/runs/${encodeURIComponent(runId)}`);
}
export async function getScannerPolicy() {
  // Public endpoint — no auth header required, but apiRequest handles that fine.
  const url = `${API_BASE_URL}/scanner/policy`;
  const r = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
  return r.json();
}

// ─── Verified Creators + Signal Channels (Vertical 3) ──────────────────────

export async function getVerifiedCreatorsLeaderboard({ verifiedOnly = false, limit = 50 } = {}) {
  const qs = new URLSearchParams({ verifiedOnly: String(verifiedOnly), limit: String(limit) });
  return apiRequest(`/verified-creators/leaderboard?${qs.toString()}`);
}
export async function getVerifiedCreatorById(userId) {
  return apiRequest(`/verified-creators/by-id/${encodeURIComponent(userId)}`);
}
export async function getVerifiedCreatorCalls(userId, limit = 50) {
  return apiRequest(`/verified-creators/by-id/${encodeURIComponent(userId)}/calls?limit=${limit}`);
}
export async function getMyVerifiedCreator() {
  return apiRequest('/verified-creators/me');
}
export async function getVerifiedCreatorPolicy() {
  return apiRequest('/verified-creators/policy');
}

export async function listSignalChannels() {
  return apiRequest('/signal-channels');
}
export async function getSignalChannel(creatorUserId) {
  return apiRequest(`/signal-channels/by-creator/${encodeURIComponent(creatorUserId)}`);
}
export async function upsertMySignalChannel(payload) {
  return apiRequest('/signal-channels', { method: 'POST', body: JSON.stringify(payload) });
}
export async function deleteMySignalChannel() {
  return apiRequest('/signal-channels', { method: 'DELETE' });
}
export async function subscribeToSignalChannel(creatorUserId) {
  return apiRequest(`/signal-channels/by-creator/${encodeURIComponent(creatorUserId)}/subscribe`, { method: 'POST', body: JSON.stringify({}) });
}
export async function unsubscribeFromSignalChannel(creatorUserId) {
  return apiRequest(`/signal-channels/by-creator/${encodeURIComponent(creatorUserId)}/unsubscribe`, { method: 'POST', body: JSON.stringify({}) });
}
export async function getSignalsFollowing() {
  return apiRequest('/signal-channels/me/following');
}
export async function getSignalsInbox(limit = 50) {
  return apiRequest(`/signal-channels/me/inbox?limit=${limit}`);
}
export async function getMySignalSubscribers() {
  return apiRequest('/signal-channels/me/subscribers');
}

// ─── Forecast Accuracy API (Vertical 2 — public data moat) ─────────────────

export async function getForecastAccuracySummary(days = 30) {
  return apiRequest(`/forecast-accuracy/summary?days=${days}`);
}
export async function getForecastAccuracyForSymbol(symbol, days = 30) {
  const qs = new URLSearchParams({ symbol, days: String(days) });
  return apiRequest(`/forecast-accuracy/by-symbol?${qs.toString()}`);
}
export async function getForecastAccuracyRecent({ days = 7, limit = 50, symbol = null } = {}) {
  const qs = new URLSearchParams({ days: String(days), limit: String(limit) });
  if (symbol) qs.set('symbol', symbol);
  return apiRequest(`/forecast-accuracy/recent?${qs.toString()}`);
}

// ─── Wallet Session API (Vertical 1 — Wallet-as-Bot) ───────────────────────

export async function getWalletSessionPolicy() {
  return apiRequest('/wallet-sessions/policy');
}
export async function prepareWalletSession(payload) {
  return apiRequest('/wallet-sessions/prepare', { method: 'POST', body: JSON.stringify(payload) });
}
export async function createWalletSession({ message, signature, chainId }) {
  return apiRequest('/wallet-sessions', { method: 'POST', body: JSON.stringify({ message, signature, chainId }) });
}
export async function listWalletSessions() {
  return apiRequest('/wallet-sessions');
}
export async function getWalletSession(sessionId) {
  return apiRequest(`/wallet-sessions/by-id/${encodeURIComponent(sessionId)}`);
}
export async function revokeWalletSession(sessionId, reason) {
  return apiRequest(`/wallet-sessions/by-id/${encodeURIComponent(sessionId)}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) });
}
export async function checkWalletSessionTrade(sessionId, proposedTrade) {
  return apiRequest(`/wallet-sessions/by-id/${encodeURIComponent(sessionId)}/check-trade`, { method: 'POST', body: JSON.stringify(proposedTrade) });
}

// ─── Cross-Exchange Arbitrage API ──────────────────────────────────────────

export async function listArbitrageSchedules() {
  return apiRequest('/arbitrage/schedules');
}
export async function saveArbitrageSchedule(payload) {
  return apiRequest('/arbitrage/schedules', { method: 'POST', body: JSON.stringify(payload) });
}
export async function getArbitrageSchedule(scheduleId) {
  return apiRequest(`/arbitrage/schedules/${scheduleId}`);
}
export async function deleteArbitrageSchedule(scheduleId) {
  return apiRequest(`/arbitrage/schedules/${scheduleId}`, { method: 'DELETE' });
}
export async function scanArbitrageOpportunity(scheduleId) {
  return apiRequest(`/arbitrage/schedules/${scheduleId}/scan`, { method: 'POST', body: JSON.stringify({}) });
}
export async function executeArbitrageNow(scheduleId) {
  return apiRequest(`/arbitrage/schedules/${scheduleId}/execute`, { method: 'POST', body: JSON.stringify({}) });
}
export async function getArbitrageHistory(scheduleId) {
  return apiRequest(`/arbitrage/schedules/${scheduleId}/history`);
}

// ─── Portfolio Rebalancer API ──────────────────────────────────────────────

export async function listRebalancerSchedules() {
  return apiRequest('/rebalancer/schedules');
}

export async function saveRebalancerSchedule(payload) {
  return apiRequest('/rebalancer/schedules', { method: 'POST', body: JSON.stringify(payload) });
}

export async function getRebalancerSchedule(scheduleId) {
  return apiRequest(`/rebalancer/schedules/${scheduleId}`);
}

export async function deleteRebalancerSchedule(scheduleId) {
  return apiRequest(`/rebalancer/schedules/${scheduleId}`, { method: 'DELETE' });
}

export async function previewRebalancerPlan(scheduleId) {
  return apiRequest(`/rebalancer/schedules/${scheduleId}/preview`, { method: 'POST', body: JSON.stringify({}) });
}

export async function runRebalancerNow(scheduleId) {
  return apiRequest(`/rebalancer/schedules/${scheduleId}/run`, { method: 'POST', body: JSON.stringify({}) });
}

export async function getRebalancerHistory(scheduleId) {
  return apiRequest(`/rebalancer/schedules/${scheduleId}/history`);
}

// ─── Algorithmic Orders API ────────────────────────────────────────────────

export async function createTwapOrder(exchangeId, symbol, side, totalAmount, durationMinutes, sliceCount) {
  return apiRequest('/algo/twap', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, side, totalAmount, durationMinutes, sliceCount }) });
}

export async function createVwapOrder(exchangeId, symbol, side, totalAmount, durationMinutes, sliceCount) {
  return apiRequest('/algo/vwap', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, side, totalAmount, durationMinutes, sliceCount }) });
}

export async function createIcebergOrder(exchangeId, symbol, side, totalAmount, visibleAmount, limitPrice) {
  return apiRequest('/algo/iceberg', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, side, totalAmount, visibleAmount, limitPrice }) });
}

export async function createBracketOrder(exchangeId, symbol, side, amount, entryPrice, takeProfitPrice, stopLossPrice) {
  return apiRequest('/algo/bracket', { method: 'POST', body: JSON.stringify({ exchangeId, symbol, side, amount, entryPrice, takeProfitPrice, stopLossPrice }) });
}

export async function getAlgoOrders() {
  return apiRequest('/algo/orders');
}

export async function cancelAlgoOrder(orderId) {
  return apiRequest(`/algo/order/${orderId}`, { method: 'DELETE' });
}

export async function getAlgoOrderStatus(orderId) {
  return apiRequest(`/algo/order/${orderId}`);
}

// ─── Portfolio Analytics API ───────────────────────────────────────────────

export async function getRiskMetrics({ days, startDate, endDate } = {}) {
  const params = new URLSearchParams();
  if (days) params.set('days', String(days));
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const qs = params.toString();
  return apiRequest(`/analytics/risk-metrics${qs ? `?${qs}` : ''}`);
}

export async function getCorrelationMatrix() {
  return apiRequest('/analytics/correlation');
}

export async function runStressTest(payload) {
  // Accepts either a string (legacy: just the scenario id) or an object
  // { scenario, btcShockPct? } for the new historical+beta engine.
  const body = typeof payload === 'string' ? { scenario: payload } : (payload || {});
  return apiRequest('/analytics/stress-test', { method: 'POST', body: JSON.stringify(body) });
}

export async function getFactorExposure() {
  return apiRequest('/analytics/factor-exposure');
}

// ─── Launch Volume Forecaster ──────────────────────────────────────────────
export async function getLaunchForecastOptions() {
  return apiRequest('/launch-volume-forecaster/options');
}

export async function runLaunchVolumeForecast({ categorySlug, targetMarketCapUsd, listingTier, days }) {
  return apiRequest('/launch-volume-forecaster/forecast', {
    method: 'POST',
    body: JSON.stringify({ categorySlug, targetMarketCapUsd, listingTier, days }),
  });
}

// ─── Multi-User Team Accounts (Elite — Trader's Suite) ────────────────────
export async function getTeamPlans() {
  return apiRequest('/team/plans');
}

export async function getMyTeam() {
  return apiRequest('/team');
}

export async function createTeam({ name, planId, billingCadence = 'annual' }) {
  return apiRequest('/team/create', {
    method: 'POST',
    body: JSON.stringify({ name, planId, billingCadence }),
  });
}

export async function getTeamMembers() {
  return apiRequest('/team/members');
}

export async function inviteTeamMember({ email, teamRole }) {
  return apiRequest('/team/invite', {
    method: 'POST',
    body: JSON.stringify({ email, teamRole }),
  });
}

export async function listTeamInvites() {
  return apiRequest('/team/invites');
}

export async function revokeTeamInvite({ inviteId }) {
  return apiRequest('/team/invite/revoke', {
    method: 'POST',
    body: JSON.stringify({ inviteId }),
  });
}

export async function previewTeamInvite({ token }) {
  return apiRequest(`/team/invite/preview?token=${encodeURIComponent(token)}`);
}

export async function acceptTeamInvite({ token }) {
  return apiRequest('/team/accept', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function removeTeamMember({ userId }) {
  return apiRequest('/team/member/remove', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

export async function changeTeamMemberRole({ userId, newRole }) {
  return apiRequest('/team/member/role', {
    method: 'POST',
    body: JSON.stringify({ userId, newRole }),
  });
}

export async function getTeamAuditLog({ limit = 100 } = {}) {
  return apiRequest(`/team/audit?limit=${limit}`);
}

export async function leaveTeam() {
  return apiRequest('/team/leave', { method: 'POST' });
}

export async function createTeamBillingCheckout({ planId, cadence = 'annual', successUrl, cancelUrl }) {
  return apiRequest('/team/billing/create-checkout', {
    method: 'POST',
    body: JSON.stringify({ planId, cadence, successUrl, cancelUrl }),
  });
}

export async function openTeamBillingPortal() {
  return apiRequest('/team/billing/portal', { method: 'POST' });
}

export async function cancelTeamBilling() {
  return apiRequest('/team/billing/cancel', { method: 'POST' });
}

// ─── Tax-Loss Auto-Harvest (Elite — Trader's Suite) ────────────────────────
// (Note: getTaxLots is exported above under "Tax Strategy" — reuse that.)
export async function getHarvestOpportunities({ minHarvestUsd, taxRatePct } = {}) {
  const qs = new URLSearchParams();
  if (minHarvestUsd != null) qs.set('minHarvestUsd', String(minHarvestUsd));
  if (taxRatePct != null) qs.set('taxRatePct', String(taxRatePct));
  const q = qs.toString();
  return apiRequest(`/tax-strategy/harvest${q ? `?${q}` : ''}`);
}

export async function planHarvest({ lotId, replacementSymbol, exchangeId, quoteSymbol }) {
  return apiRequest('/tax-strategy/harvest/plan', {
    method: 'POST',
    body: JSON.stringify({ lotId, replacementSymbol, exchangeId, quoteSymbol }),
  });
}

export async function executeHarvest({ lotId, replacementSymbol, exchangeId, quoteSymbol, acknowledgeWashSale = false }) {
  return apiRequest('/tax-strategy/harvest/execute', {
    method: 'POST',
    body: JSON.stringify({ lotId, replacementSymbol, exchangeId, quoteSymbol, acknowledgeWashSale }),
  });
}

export async function getHarvestHistory({ limit = 50 } = {}) {
  return apiRequest(`/tax-strategy/harvest/history?limit=${limit}`);
}

// ─── Token Launch Scanner (Elite — Trader's Suite) ─────────────────────────
export async function getTokenScannerChains() {
  return apiRequest('/token-scanner/chains');
}
export async function scanToken({ address, chain = 'ethereum' }) {
  return apiRequest(`/token-scanner/scan?address=${encodeURIComponent(address)}&chain=${encodeURIComponent(chain)}`);
}
export async function listRecentTokenScans() {
  return apiRequest('/token-scanner/recent');
}
export async function getTokenScan(scanId) {
  return apiRequest(`/token-scanner/recent/${encodeURIComponent(scanId)}`);
}
export async function deleteTokenScan(scanId) {
  return apiRequest(`/token-scanner/recent/${encodeURIComponent(scanId)}`, { method: 'DELETE' });
}

// ─── Uniswap V3 LP Studio (Elite — Trader's Suite) ─────────────────────────
export async function getLpStudioChains() {
  return apiRequest('/lp-studio/chains');
}
export async function getLpStudioPositions({ wallet, chain = 'ethereum' }) {
  return apiRequest(`/lp-studio/positions?wallet=${encodeURIComponent(wallet)}&chain=${encodeURIComponent(chain)}`);
}
export async function simulateLpRebalance(payload) {
  return apiRequest('/lp-studio/simulate-rebalance', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listLpSavedViews() {
  return apiRequest('/lp-studio/saved-views');
}
export async function saveLpSavedView({ label, wallet, chain }) {
  return apiRequest('/lp-studio/saved-views', { method: 'POST', body: JSON.stringify({ label, wallet, chain }) });
}
export async function deleteLpSavedView(id) {
  return apiRequest(`/lp-studio/saved-views/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ─── Quant Lab (Elite — Trader's Suite) ────────────────────────────────────
export async function quantLabParameterSweep(payload) {
  return apiRequest('/quant-lab/parameter-sweep', { method: 'POST', body: JSON.stringify(payload) });
}
export async function quantLabWalkForward(payload) {
  return apiRequest('/quant-lab/walk-forward', { method: 'POST', body: JSON.stringify(payload) });
}
export async function quantLabMonteCarlo(payload) {
  return apiRequest('/quant-lab/monte-carlo', { method: 'POST', body: JSON.stringify(payload) });
}
export async function quantLabMultiSymbol(payload) {
  return apiRequest('/quant-lab/multi-symbol', { method: 'POST', body: JSON.stringify(payload) });
}
export async function quantLabComparison(payload) {
  return apiRequest('/quant-lab/comparison', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listQuantLabExperiments() {
  return apiRequest('/quant-lab/experiments');
}
export async function saveQuantLabExperiment({ name, tool, input, result }) {
  return apiRequest('/quant-lab/experiments', { method: 'POST', body: JSON.stringify({ name, tool, input, result }) });
}
export async function getQuantLabExperiment(id) {
  return apiRequest(`/quant-lab/experiments/${encodeURIComponent(id)}`);
}
export async function deleteQuantLabExperiment(id) {
  return apiRequest(`/quant-lab/experiments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ─── AI Strategy Coach (Elite — Trader's Suite) ────────────────────────────
export async function coachChat({ message, conversationId } = {}) {
  return apiRequest('/coach/chat', {
    method: 'POST',
    body: JSON.stringify({ message, conversationId }),
  });
}

export async function coachCoachBot({ kind, botId, message, conversationId } = {}) {
  return apiRequest('/coach/coach-bot', {
    method: 'POST',
    body: JSON.stringify({ kind, botId, message, conversationId }),
  });
}

export async function listCoachConversations() {
  return apiRequest('/coach/conversations');
}

export async function getCoachConversation(conversationId) {
  return apiRequest(`/coach/conversations/${encodeURIComponent(conversationId)}`);
}

export async function deleteCoachConversation(conversationId) {
  return apiRequest(`/coach/conversations/${encodeURIComponent(conversationId)}`, { method: 'DELETE' });
}

// ─── Institutional Reports (Elite — Trader's Suite) ────────────────────────
export async function getReportTemplates() {
  return apiRequest('/reports/templates');
}

export async function generateReport({ template, inputs }) {
  return apiRequest('/reports/generate', {
    method: 'POST',
    body: JSON.stringify({ template, inputs }),
  });
}

export async function getReportHistory({ limit = 50 } = {}) {
  return apiRequest(`/reports/history?limit=${limit}`);
}

export async function deleteReport(reportId) {
  return apiRequest(`/reports/${encodeURIComponent(reportId)}`, { method: 'DELETE' });
}

/**
 * Download a generated report PDF. Triggers a browser download via blob URL.
 * Returns the suggested filename used.
 */
export async function downloadReport(reportId, suggestedFilename) {
  const url = `${API_BASE_URL}/reports/${encodeURIComponent(reportId)}/download`;
  const res = await fetch(url, { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try { const j = await res.json(); msg = j?.error?.message || msg; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = suggestedFilename || `${reportId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  return link.download;
}

// ─── Multi-Account & Multi-Wallet Aggregator (Elite — Trader's Suite) ─────
export async function getAggregatorSummary({ refresh = false } = {}) {
  const qs = refresh ? '?refresh=1' : '';
  return apiRequest(`/aggregator/summary${qs}`);
}

export async function getAggregatorByAsset() {
  return apiRequest('/aggregator/by-asset');
}

export async function getAggregatorPnl({ days, startDate, endDate } = {}) {
  if (startDate && endDate) {
    return apiRequest(`/aggregator/pnl?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
  }
  const d = Math.max(7, Math.min(365, Number(days) || 90));
  return apiRequest(`/aggregator/pnl?days=${d}`);
}

// ─── Listing Readiness Scorecard ───────────────────────────────────────────
export async function scoreListingReadiness({ contract, chain, attested }) {
  return apiRequest('/listing-readiness/score', {
    method: 'POST',
    body: JSON.stringify({ contract, chain, attested }),
  });
}

export async function getListingReadinessChains() {
  return apiRequest('/listing-readiness/chains');
}

// ─── DEX Liquidity Depth Optimizer ─────────────────────────────────────────
export async function computeDexLiquidity(input) {
  return apiRequest('/dex-liquidity-optimizer/compute', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getDexSpotPrice(symbol) {
  return apiRequest(`/dex-liquidity-optimizer/spot-price?symbol=${encodeURIComponent(symbol)}`);
}

// ─── Position integrity ────────────────────────────────────────────────────
export async function getPositionDrift({ exchangeId, asset }) {
  const params = new URLSearchParams();
  if (exchangeId) params.set('exchangeId', exchangeId);
  if (asset) params.set('asset', asset);
  return apiRequest(`/positions/drift?${params.toString()}`);
}

export async function reconcilePosition({ exchangeId, asset, closePriceUsd } = {}) {
  return apiRequest('/positions/reconcile', {
    method: 'POST',
    body: JSON.stringify({
      exchangeId, asset,
      ...(Number.isFinite(closePriceUsd) ? { closePriceUsd } : {}),
      confirmed: true,
    }),
  });
}

export async function getKellyCriterion() {
  return apiRequest('/analytics/kelly-criterion');
}

export async function getAiCommentary({ days } = {}) {
  const params = new URLSearchParams();
  if (days) params.set('days', String(days));
  const qs = params.toString();
  return apiRequest(`/analytics/ai-commentary${qs ? `?${qs}` : ''}`);
}

export async function getPairsTradingOpportunities({ force = false, source = null } = {}) {
  const params = new URLSearchParams();
  if (force) params.set('force', 'true');
  if (source) params.set('source', source);
  const qs = params.toString();
  return apiRequest(`/analytics/pairs-trading${qs ? '?' + qs : ''}`);
}

export async function getYieldOpportunities() {
  return apiRequest('/analytics/yield-opportunities');
}

// ─── Whale Watcher API ─────────────────────────────────────────────────────

export async function getWhaleAlerts(chain = 'ethereum', minUsd = 100000) {
  return apiRequest(`/whale/alerts?chain=${chain}&minUsd=${minUsd}`);
}

export async function getSmartMoneyWallets(chain = 'ethereum', minUsd = 250000) {
  return apiRequest(`/whale/smart-money?chain=${chain}&minUsd=${minUsd}`);
}

export async function getAccumulationTrends(chain = 'ethereum', minUsd = 100000) {
  return apiRequest(`/whale/accumulation?chain=${chain}&minUsd=${minUsd}`);
}

export async function getDarkPoolQuote(symbol, amount, side, chain) {
  return apiRequest('/whale/dark-pool-quote', { method: 'POST', body: JSON.stringify({ symbol, amount, side, chain }) });
}

// ─── Paid Channels API ─────────────────────────────────────────────────────

export async function getPaidChannelStatus() {
  return apiRequest('/paid-channels/status');
}

export async function checkPaidChannelEligibility() {
  return apiRequest('/paid-channels/check-eligibility', { method: 'POST' });
}

export async function applyForPaidChannel(channelId, application) {
  return apiRequest('/paid-channels/apply', { method: 'POST', body: JSON.stringify({ channelId, ...application }) });
}

export async function getMyPaidChannelApplications() {
  return apiRequest('/paid-channels/my-applications');
}

export async function getPaidChannelTrustStats(channelId) {
  return apiRequest(`/paid-channels/trust/${channelId}`);
}

export async function getPaidChannelInfo(channelId) {
  return apiRequest(`/paid-channels/channel/${channelId}`);
}

export async function listPaidChannels() {
  return apiRequest('/paid-channels/list');
}

// Paid Channel Admin
export async function getPaidChannelPending() {
  return apiRequest('/paid-channels/admin/pending');
}

export async function approvePaidChannelApp(applicationId, notes) {
  return apiRequest('/paid-channels/admin/approve', { method: 'POST', body: JSON.stringify({ applicationId, notes }) });
}

export async function rejectPaidChannelApp(applicationId, reason) {
  return apiRequest('/paid-channels/admin/reject', { method: 'POST', body: JSON.stringify({ applicationId, reason }) });
}

export async function togglePaidChannelFlag(flagName, value) {
  return apiRequest('/paid-channels/admin/toggle', { method: 'POST', body: JSON.stringify({ flagName, value }) });
}

export async function getPaidChannelFlags() {
  return apiRequest('/paid-channels/admin/flags');
}

export async function managePaidChannelAllowlist(action, userId) {
  return apiRequest('/paid-channels/admin/allowlist', { method: 'POST', body: JSON.stringify({ action, userId }) });
}

// Channel Subscriptions
export async function subscribeToChannel(channelId, tierId) {
  return apiRequest('/channel-subs/subscribe', { method: 'POST', body: JSON.stringify({ channelId, tierId }) });
}

export async function confirmChannelSubPayment(subscriptionId, txHash, amount, chain) {
  return apiRequest('/channel-subs/confirm-payment', { method: 'POST', body: JSON.stringify({ subscriptionId, txHash, amount, chain }) });
}

export async function cancelChannelSub(subscriptionId) {
  return apiRequest('/channel-subs/cancel', { method: 'POST', body: JSON.stringify({ subscriptionId }) });
}

export async function getMyChannelSubscriptions() {
  return apiRequest('/channel-subs/my-subscriptions');
}

export async function checkChannelSubscription(channelId) {
  return apiRequest(`/channel-subs/check/${channelId}`);
}

// ─── Beta Framework API ────────────────────────────────────────────────────

export async function getBetaFeatures() {
  return apiRequest('/beta/features');
}

export async function getBetaFeature(id) {
  return apiRequest(`/beta/feature/${id}`);
}

export async function getMyAcknowledgments() {
  return apiRequest('/beta/my-acks');
}

export async function acknowledgeBetaFeature(featureId) {
  return apiRequest('/beta/acknowledge', { method: 'POST', body: JSON.stringify({ featureId }) });
}

export async function submitBetaFeedback(featureId, rating, comment) {
  return apiRequest('/beta/feedback', { method: 'POST', body: JSON.stringify({ featureId, rating, comment }) });
}

export async function getMyBetaFeedback() {
  return apiRequest('/beta/my-feedback');
}

export async function getHelpArticles() {
  return apiRequest('/beta/help/articles');
}

export async function getHelpArticle(id) {
  return apiRequest(`/beta/help/article/${id}`);
}

// ─── TradingView Webhooks API ──────────────────────────────────────────────

export async function listWebhooks() { return apiRequest('/webhooks/tradingview'); }
export async function createWebhook(config) {
  return apiRequest('/webhooks/tradingview', { method: 'POST', body: JSON.stringify(config) });
}
export async function updateWebhook(webhookKey, updates) {
  return apiRequest(`/webhooks/tradingview/${webhookKey}`, { method: 'PATCH', body: JSON.stringify(updates) });
}
export async function deleteWebhook(webhookKey) {
  return apiRequest(`/webhooks/tradingview/${webhookKey}`, { method: 'DELETE' });
}
export async function testWebhook(webhookKey) {
  return apiRequest(`/webhooks/tradingview/${webhookKey}/test`, { method: 'POST' });
}
export async function getWebhookLog(webhookKey) {
  return apiRequest(`/webhooks/tradingview/${webhookKey}/log`);
}

// ─── Backtest API ──────────────────────────────────────────────────────────

export async function runRealBacktest(params) {
  return apiRequest('/backtest/run', { method: 'POST', body: JSON.stringify(params) });
}
export async function getRecentBacktests() { return apiRequest('/backtest/recent'); }
export async function getBacktestResult(backtestId) { return apiRequest(`/backtest/${backtestId}`); }

// ─── Natural Language Bot Config ───────────────────────────────────────────

export async function buildBotFromDescription(description, conversationHistory = []) {
  return apiRequest('/nl-bot/build', { method: 'POST', body: JSON.stringify({ description, conversationHistory }) });
}
export async function refineBotConfig(currentConfig, refinementRequest, conversationHistory = []) {
  return apiRequest('/nl-bot/refine', { method: 'POST', body: JSON.stringify({ currentConfig, refinementRequest, conversationHistory }) });
}

// ─── Learning Library ──────────────────────────────────────────────────────

export async function listLibraryGuides() { return apiRequest('/library/guides'); }
export async function getLibraryGuide(guideId) { return apiRequest(`/library/guide/${guideId}`); }
export async function getGuidesByFeature(featureId) { return apiRequest(`/library/by-feature/${featureId}`); }

// ─── Dashboard Snapshot (batched poll endpoint) ────────────────────────────
// Returns bots + status + alerts + portfolio + recent trades in a single call.
// Replaces 4 separate polls with 1 — saves ~75% of dashboard API traffic.
export async function getDashboardSnapshot() { return apiRequest('/dashboard/snapshot'); }

// ─── Grid Bot ──────────────────────────────────────────────────────────────

export async function previewGrid({ lowPrice, highPrice, gridCount, totalAmount, mode = 'arithmetic' }) {
  const qs = new URLSearchParams({ lowPrice, highPrice, gridCount, totalAmount, mode });
  return apiRequest(`/grid/preview?${qs.toString()}`);
}
export async function deployGridBot(config) {
  // Grid deploy places one CCXT order per grid level (often 10-50 levels) and
  // each call is rate-limited by the exchange — Coinbase alone can take ~1-3s
  // per order placement. The default 25s timeout fires before deploy completes
  // even though the bot DOES get created and starts firing notifications.
  // Bumping to 120s covers up to ~50 orders on the slowest supported exchange.
  return apiRequest('/grid/deploy', { method: 'POST', body: JSON.stringify(config), timeoutMs: 120000 });
}
export async function listGridBots() { return apiRequest('/grid/bots'); }
export async function getGridBot(botId) { return apiRequest(`/grid/bots/${botId}`); }
export async function getGridBotStats(botId) { return apiRequest(`/grid/bots/${botId}/stats`); }
export async function stopGridBot(botId) {
  return apiRequest(`/grid/bots/${botId}/stop`, { method: 'POST', body: JSON.stringify({}) });
}
export async function syncGridBot(botId) {
  return apiRequest(`/grid/bots/${botId}/sync`, { method: 'POST', body: JSON.stringify({}) });
}
// Toggle auto-recover on an existing grid bot. When enabled, the worker
// redeploys orders automatically once activeOrders becomes empty AND current
// price is back inside the bot's configured range (rate-limited to once/hour).
export async function setGridBotAutoRecover(botId, enabled) {
  return apiRequest(`/grid/bots/${botId}/auto-recover`, {
    method: 'POST',
    body: JSON.stringify({ enabled: !!enabled }),
  });
}
export async function listGridTypes() { return apiRequest('/grid/types'); }

// ─── Smart Trade Terminal ──────────────────────────────────────────────────

export async function listSmartTrades() { return apiRequest('/smart-trade/trades'); }
export async function getSmartTrade(tradeId) { return apiRequest(`/smart-trade/trades/${tradeId}`); }
export async function getSmartTradeStats(tradeId) { return apiRequest(`/smart-trade/trades/${tradeId}/stats`); }
export async function openSmartTrade(config) {
  return apiRequest('/smart-trade/open', { method: 'POST', body: JSON.stringify(config) });
}
export async function closeSmartTrade(tradeId) {
  return apiRequest(`/smart-trade/trades/${tradeId}/close`, { method: 'POST', body: JSON.stringify({}) });
}
export async function syncSmartTrade(tradeId) {
  return apiRequest(`/smart-trade/trades/${tradeId}/sync`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── MEXC Disclaimer ───────────────────────────────────────────────────────

export async function getMexcDisclaimerStatus() { return apiRequest('/mexc-disclaimer/status'); }
export async function acknowledgeMexcDisclaimer() {
  return apiRequest('/mexc-disclaimer/acknowledge', { method: 'POST', body: JSON.stringify({ confirmed: true }) });
}

// ─── Verified Track Record ─────────────────────────────────────────────────

export async function getMyTrackRecord() { return apiRequest('/track-record/me'); }
export async function refreshMyTrackRecord() {
  return apiRequest('/track-record/refresh', { method: 'POST', body: JSON.stringify({}) });
}
export async function getUserTrackRecordBadge(userId) {
  return apiRequest(`/track-record/user/${userId}/badge`);
}

// ─── Onboarding Wizard ─────────────────────────────────────────────────────

export async function getWizardStatus() { return apiRequest('/wizard/status'); }
export async function saveWizardStep(stepId, data = {}) {
  return apiRequest('/wizard/step', { method: 'POST', body: JSON.stringify({ stepId, data }) });
}
export async function completeWizard() {
  return apiRequest('/wizard/complete', { method: 'POST', body: JSON.stringify({}) });
}
export async function skipWizard() {
  return apiRequest('/wizard/skip', { method: 'POST', body: JSON.stringify({}) });
}
export async function getWizardRecommendations(profile) {
  return apiRequest(`/wizard/recommendations?profile=${profile}`);
}

// ─── Outbound Webhooks ─────────────────────────────────────────────────────

export async function listOutboundWebhooks() { return apiRequest('/outbound-webhooks'); }
export async function getOutboundWebhookEventTypes() { return apiRequest('/outbound-webhooks/event-types'); }
export async function createOutboundWebhook(payload) {
  return apiRequest('/outbound-webhooks', { method: 'POST', body: JSON.stringify(payload) });
}
export async function updateOutboundWebhook(id, payload) {
  return apiRequest(`/outbound-webhooks/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}
export async function deleteOutboundWebhook(id) {
  return apiRequest(`/outbound-webhooks/${id}`, { method: 'DELETE' });
}
export async function testOutboundWebhook(id) {
  return apiRequest(`/outbound-webhooks/${id}/test`, { method: 'POST', body: JSON.stringify({}) });
}
export async function getOutboundWebhookDeliveries(id) {
  return apiRequest(`/outbound-webhooks/${id}/deliveries`);
}

// ─── FX Rates ──────────────────────────────────────────────────────────────

export async function getFxRates(base = 'USD') { return apiRequest(`/fx/rates?base=${base}`); }

// ─── Strategy Heatmap ──────────────────────────────────────────────────────

export async function getStrategyHeatmap(source = 'all') { return apiRequest(`/strategy-heatmap/me?source=${source}`); }

// ─── News Feed ─────────────────────────────────────────────────────────────

export async function getNewsFeed({ filter = 'hot', currencies = '', kind = 'news' } = {}) {
  const qs = new URLSearchParams({ filter, currencies, kind });
  return apiRequest(`/news/feed?${qs.toString()}`);
}
export async function getNewsSentimentSummary(symbol) {
  return apiRequest(`/news/sentiment-summary?symbol=${encodeURIComponent(symbol)}`);
}

// ─── Coin Data (CoinGecko proxy + community overlay) ───────────────────────

// Route is `/coins/search` (plural) — `/coin/search` would shadow the
// `/coin/{coinId}` parameterized route on Azure Functions.
export async function searchCoins(q) { return apiRequest(`/coins/search?q=${encodeURIComponent(q)}`); }

// Build the unsigned swap transaction via Paraswap. The frontend signs the
// returned tx with the user's wallet (wagmi) and submits the resulting txHash
// to executeSwap() for backend tracking.
export async function buildSwapTx({ chain, fromToken, toToken, amount, slippage, userAddress, fromDecimals, toDecimals }) {
  return apiRequest('/swap/build-tx', {
    method: 'POST',
    body: JSON.stringify({ chain, fromToken, toToken, amount, slippage, userAddress, fromDecimals, toDecimals }),
  });
}
export async function getCoinDetail(coinId) { return apiRequest(`/coin/${encodeURIComponent(coinId)}`); }
export async function getCoinCommunity(coinId) { return apiRequest(`/coin/${encodeURIComponent(coinId)}/community`); }

// ─── Trading Journal ───────────────────────────────────────────────────────

export async function listJournalEntries({ limit = 50, source = null } = {}) {
  const qs = new URLSearchParams({ limit });
  if (source) qs.set('source', source);
  return apiRequest(`/journal/entries?${qs.toString()}`);
}
export async function getJournalEntry(tradeId) { return apiRequest(`/journal/entry/${encodeURIComponent(tradeId)}`); }
export async function upsertJournalEntry(tradeId, payload) {
  return apiRequest(`/journal/entry/${encodeURIComponent(tradeId)}`, { method: 'POST', body: JSON.stringify(payload) });
}
export async function deleteJournalEntry(tradeId) {
  return apiRequest(`/journal/entry/${encodeURIComponent(tradeId)}`, { method: 'DELETE' });
}
export async function searchJournal(q) { return apiRequest(`/journal/search?q=${encodeURIComponent(q)}`); }
export async function getJournalRecentTrades() { return apiRequest('/journal/recent-trades'); }

// ─── Community Live Feed (Phase 2) ────────────────────────────────────────
export async function getLiveFeed({ limit = 50, event = null } = {}) {
  const qs = new URLSearchParams({ limit });
  if (event) qs.set('event', event);
  return apiRequest(`/community/live-feed?${qs.toString()}`);
}
export async function getLiveFeedOptInStatus() {
  return apiRequest('/community/live-feed/opt-in-status');
}
export async function setLiveFeedOptIn(enabled, displayName = null) {
  return apiRequest('/community/live-feed/opt-in', {
    method: 'POST',
    body: JSON.stringify({ enabled, displayName }),
  });
}

// ─── Phase 3: Official Channels Broadcast ─────────────────────────────────
export async function getBroadcastChannels() {
  return apiRequest('/community/broadcast/channels');
}
export async function getBroadcastMyPrefs() {
  return apiRequest('/community/broadcast/my-prefs');
}
export async function setBroadcastPrefs({ enabled, displayName = null, filters = null }) {
  return apiRequest('/community/broadcast/opt-in', {
    method: 'POST',
    body: JSON.stringify({ enabled, displayName, filters }),
  });
}
export async function getBroadcastAdminConfig() {
  return apiRequest('/community/broadcast/admin/config');
}
export async function setBroadcastAdminConfig({ channelId, discordUrl, telegramUrl }) {
  return apiRequest('/community/broadcast/admin/config', {
    method: 'POST',
    body: JSON.stringify({ channelId, discordUrl, telegramUrl }),
  });
}

// ─── Tax CSV Export ────────────────────────────────────────────────────────
// This returns a CSV blob — caller needs the raw blob, not JSON
export function getTaxCsvUrl({ startDate, endDate }) {
  const qs = new URLSearchParams();
  if (startDate) qs.set('startDate', startDate);
  if (endDate) qs.set('endDate', endDate);
  const apiUrl = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
  return `${apiUrl}/tax-export/csv?${qs.toString()}`;
}

// ─── Trader Leaderboard (user-vs-user, separate from token whale leaderboard) ──

export async function getTraderLeaderboard(category = 'return', limit = 50) {
  return apiRequest(`/leaderboard/traders?category=${category}&limit=${limit}`);
}
export async function getLeaderboardCategories() { return apiRequest('/leaderboard/categories'); }
export async function getLeaderboardMyStatus() { return apiRequest('/leaderboard/my-status'); }
export async function setLeaderboardOptIn(enabled, displayName = null) {
  return apiRequest('/leaderboard/opt-in', { method: 'POST', body: JSON.stringify({ enabled, displayName }) });
}

// ─── Position Sizing Calculator ────────────────────────────────────────────

export async function calculatePositionSize(input) {
  return apiRequest('/position-sizing/calculate', { method: 'POST', body: JSON.stringify(input) });
}

// ─── DCA Safety Bot ────────────────────────────────────────────────────────

export async function previewDcaSafety(config) {
  return apiRequest('/dca-safety/preview', { method: 'POST', body: JSON.stringify(config) });
}
export async function deployDcaSafetyBot(config) {
  // DCA Safety places an initial base order + up to N safety orders on
  // the exchange. Same rate-limit math as grid deploy — bump timeout to
  // 120s so the call doesn't visibly time out while orders place.
  return apiRequest('/dca-safety/deploy', { method: 'POST', body: JSON.stringify(config), timeoutMs: 120000 });
}
export async function listDcaSafetyBots() { return apiRequest('/dca-safety/bots'); }
export async function getDcaSafetyBot(botId) { return apiRequest(`/dca-safety/bots/${botId}`); }
export async function getDcaSafetyBotStats(botId) { return apiRequest(`/dca-safety/bots/${botId}/stats`); }
export async function stopDcaSafetyBot(botId) {
  return apiRequest(`/dca-safety/bots/${botId}/stop`, { method: 'POST', body: JSON.stringify({}) });
}
export async function syncDcaSafetyBot(botId) {
  return apiRequest(`/dca-safety/bots/${botId}/sync`, { method: 'POST', body: JSON.stringify({}) });
}
// Toggle auto-restart-cycle on an existing DCA Safety bot. When enabled, the
// bot starts a fresh cycle the moment TP fills, instead of marking completed.
export async function setDcaSafetyAutoRecover(botId, enabled) {
  return apiRequest(`/dca-safety/bots/${botId}/auto-recover`, {
    method: 'POST',
    body: JSON.stringify({ enabled: !!enabled }),
  });
}

// ─── Stress Scenarios ──────────────────────────────────────────────────────

export async function listStressScenarios() { return apiRequest('/backtest-library/scenarios'); }
export async function runStressScenario(payload) {
  return apiRequest('/backtest-library/run-scenario', { method: 'POST', body: JSON.stringify(payload) });
}

// ─── Conditional / Composite Bots ──────────────────────────────────────────

export async function listConditionalStrategies() { return apiRequest('/conditional/strategies'); }
export async function getConditionalStrategy(id)  { return apiRequest(`/conditional/strategies/${id}`); }
export async function createConditionalStrategy(config) {
  return apiRequest('/conditional/strategies', { method: 'POST', body: JSON.stringify(config) });
}
export async function activateConditionalStrategy(id) {
  return apiRequest(`/conditional/strategies/${id}/activate`, { method: 'POST', body: JSON.stringify({}) });
}
export async function pauseConditionalStrategy(id) {
  return apiRequest(`/conditional/strategies/${id}/pause`, { method: 'POST', body: JSON.stringify({}) });
}
export async function deleteConditionalStrategy(id) {
  return apiRequest(`/conditional/strategies/${id}`, { method: 'DELETE' });
}
export async function evaluateConditionalStrategy(id) {
  return apiRequest(`/conditional/strategies/${id}/evaluate`, { method: 'POST', body: JSON.stringify({}) });
}
export async function simulateConditionalStrategy(config) {
  return apiRequest('/conditional/simulate', { method: 'POST', body: JSON.stringify(config) });
}
// Phase 1 (2026-06-08) — toggle auto-execution on a conditional strategy.
// When ON, the engine actually deploys / stops the inner bot listed in the
// active rule's thenBotConfig on every regime switch.
export async function setConditionalAutoExecute(id, enabled) {
  return apiRequest(`/conditional/strategies/${id}/auto-execute`, {
    method: 'POST',
    body: JSON.stringify({ enabled: !!enabled }),
  });
}
// Returns the catalog of inner bot types the engine can auto-deploy. Phase 1
// returns just Grid Bot. UI uses this to render the per-rule action dropdown.
export async function listConditionalInnerBotTypes() {
  return apiRequest('/conditional/inner-bot-types');
}

// ───── Unified User Activity (single source of truth for Dashboard / Analytics / Intelligence) ─────
// Aggregates real trades from: CEX Trading, Smart Trade, Algo Orders, DEX Swaps,
// DEX Limit Orders, Futures. No bot fantasy — every metric comes from real fills.

export async function getUserActivitySummary({ recent = 20, days = 30, window = null } = {}) {
  // `window` filters charts/tables/behavior to last N days (or 'all' for no filter).
  // `days` controls the time-series length when no window is set.
  const params = new URLSearchParams();
  params.set('recent', String(recent));
  params.set('days', String(days));
  if (window !== null && window !== undefined) params.set('window', String(window));
  return apiRequest(`/user-activity/summary?${params.toString()}`);
}

export async function getUserActivityRecent({ limit = 50 } = {}) {
  return apiRequest(`/user-activity/recent?limit=${limit}`);
}

export async function getUserActivityTimeSeries({ days = 30 } = {}) {
  return apiRequest(`/user-activity/time-series?days=${days}`);
}

export async function getUserActivityByExchange() {
  return apiRequest('/user-activity/by-exchange');
}

export async function getUserActivityBySymbol() {
  return apiRequest('/user-activity/by-symbol');
}

export async function getUserActivityBehavior() {
  return apiRequest('/user-activity/behavior');
}

// ───── Market data (symbol-direct, NOT bot-coupled) ─────
// Real OHLCV via Binance → indicator + forecast + signal engines.
// Used by Market Trend, Forecast, Signal pages.

export async function getMarketTrend(symbol) {
  return apiRequest(`/market/trend?symbol=${encodeURIComponent(symbol)}`);
}

export async function getMarketIndicators(symbol) {
  return apiRequest(`/market/indicators?symbol=${encodeURIComponent(symbol)}`);
}

export async function getMarketForecast(symbol) {
  return apiRequest(`/market/forecast?symbol=${encodeURIComponent(symbol)}`);
}

export async function getMarketSignal(symbol) {
  return apiRequest(`/market/signal?symbol=${encodeURIComponent(symbol)}`);
}

// ─── Cost-Basis P&L ────────────────────────────────────────────────────────
// FIFO/LIFO/HIFO matched realized P&L, open positions, daily P&L series,
// and tax-lot report. Backs every "real" Portfolio Analytics metric.

export async function getCostBasisSummary({ method = 'FIFO' } = {}) {
  return apiRequest(`/cost-basis/summary?method=${encodeURIComponent(method)}`);
}
export async function getCostBasisPositions({ method = 'FIFO' } = {}) {
  return apiRequest(`/cost-basis/positions?method=${encodeURIComponent(method)}`);
}
export async function getCostBasisDailyPnl({ method = 'FIFO' } = {}) {
  return apiRequest(`/cost-basis/daily-pnl?method=${encodeURIComponent(method)}`);
}
export async function getCostBasisClosedTrades({ method = 'FIFO', limit = 200 } = {}) {
  return apiRequest(`/cost-basis/closed-trades?method=${encodeURIComponent(method)}&limit=${limit}`);
}
export async function getCostBasisTaxReport({ method = 'FIFO', year = null } = {}) {
  const params = new URLSearchParams();
  params.set('method', method);
  if (year) params.set('year', String(year));
  return apiRequest(`/cost-basis/tax-report?${params.toString()}`);
}

// ─── Portfolio Advanced Analytics (premium tabs) ───────────────────────────

export async function getPortfolioBenchmark({ benchmark = 'BTC', days = 90 } = {}) {
  return apiRequest(`/portfolio-advanced/benchmark?benchmark=${encodeURIComponent(benchmark)}&days=${days}`);
}
export async function getPortfolioHodlVsTrade() {
  return apiRequest('/portfolio-advanced/hodl-vs-trade');
}
export async function getPortfolioCorrelation({ days = 60, limit = 8 } = {}) {
  return apiRequest(`/portfolio-advanced/correlation?days=${days}&limit=${limit}`);
}
export async function getPortfolioFeeImpact() {
  return apiRequest('/portfolio-advanced/fee-impact');
}

// ─── DEX Limit Orders v2 (1inch LOP) ───────────────────────────────────────
// Net-new — isolated DEX automation surface. See backend/src/shared/
// oneInchLopAdapter.js for the integration. Does not touch the legacy
// /swap → Limit Orders price-alarm flow.
export async function prepareDexLimitOrder(payload) {
  return apiRequest('/dex/limit-orders/prepare', { method: 'POST', body: JSON.stringify(payload) });
}
export async function submitDexLimitOrder(payload) {
  return apiRequest('/dex/limit-orders/submit', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexLimitOrders(status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiRequest(`/dex/limit-orders${qs}`);
}
export async function cancelDexLimitOrder(orderHash) {
  return apiRequest(`/dex/limit-orders/${encodeURIComponent(orderHash)}/cancel`, { method: 'POST', body: JSON.stringify({}) });
}
export async function refreshDexLimitOrder(orderHash) {
  return apiRequest(`/dex/limit-orders/${encodeURIComponent(orderHash)}/refresh`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── DEX DCA (Phase 1.5) ────────────────────────────────────────────────────
// Recurring scheduled buys via 1inch LOP / CoW. User signs N orders upfront,
// worker submits each at scheduledAt.
export async function quoteDexDcaPlan(payload) {
  return apiRequest('/dex/dca/quote-plan', { method: 'POST', body: JSON.stringify(payload) });
}
export async function prepareDexDcaSlice(payload) {
  return apiRequest('/dex/dca/prepare-slice', { method: 'POST', body: JSON.stringify(payload) });
}
export async function commitDexDcaPlan(payload) {
  return apiRequest('/dex/dca/commit-plan', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexDcaPlans() {
  return apiRequest('/dex/dca');
}
export async function cancelDexDcaPlan(planId) {
  return apiRequest(`/dex/dca/${encodeURIComponent(planId)}/cancel`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── DEX Stop Orders (Phase 2) ──────────────────────────────────────────────
// Stop-loss + take-profit. Chainlink-triggered; signed order held off-chain
// until the price crosses the trigger, then submitted to CoW/Velora orderbook.
export async function prepareDexStopOrder(payload) {
  return apiRequest('/dex/stop-orders/prepare', { method: 'POST', body: JSON.stringify(payload) });
}
export async function submitDexStopOrder(payload) {
  return apiRequest('/dex/stop-orders/submit', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexStopOrders() {
  return apiRequest('/dex/stop-orders');
}
export async function cancelDexStopOrder(stopOrderId) {
  return apiRequest(`/dex/stop-orders/${encodeURIComponent(stopOrderId)}/cancel`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── DEX Smart Trade (Phase 3) ──────────────────────────────────────────────
// Entry + TP + SL composed in one signed bundle. User signs 3 EIP-712 orders
// upfront; backend persists; worker activates TP/SL on entry fill.
export async function prepareDexSmartTrade(payload) {
  return apiRequest('/dex/smart-trades/prepare', { method: 'POST', body: JSON.stringify(payload) });
}
export async function commitDexSmartTrade(payload) {
  return apiRequest('/dex/smart-trades/commit', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexSmartTrades() {
  return apiRequest('/dex/smart-trades');
}
export async function cancelDexSmartTrade(smartTradeId) {
  return apiRequest(`/dex/smart-trades/${encodeURIComponent(smartTradeId)}/cancel`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── DEX Grid Bot (Phase 4a) ────────────────────────────────────────────────
// Buy-low / sell-high across a price range. User signs 2*(N-1) orders upfront
// (one buy + one sell per pair). Initial buys submitted to orderbook; sells
// queued in a pre-signed pool; worker activates each sell when its paired buy
// fills, then signals the user when the pool runs low so they can extend.
export async function prepareDexGridBot(payload) {
  return apiRequest('/dex/grid-bots/prepare', { method: 'POST', body: JSON.stringify(payload) });
}
export async function commitDexGridBot(payload) {
  return apiRequest('/dex/grid-bots/commit', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexGridBots() {
  return apiRequest('/dex/grid-bots');
}
export async function getDexGridBot(gridBotId) {
  return apiRequest(`/dex/grid-bots/${encodeURIComponent(gridBotId)}`);
}
export async function pauseDexGridBot(gridBotId) {
  return apiRequest(`/dex/grid-bots/${encodeURIComponent(gridBotId)}/pause`, { method: 'POST', body: JSON.stringify({}) });
}
export async function resumeDexGridBot(gridBotId) {
  return apiRequest(`/dex/grid-bots/${encodeURIComponent(gridBotId)}/resume`, { method: 'POST', body: JSON.stringify({}) });
}
export async function cancelDexGridBot(gridBotId) {
  return apiRequest(`/dex/grid-bots/${encodeURIComponent(gridBotId)}/cancel`, { method: 'POST', body: JSON.stringify({}) });
}
export async function refillPrepareDexGridBot(gridBotId, payload = {}) {
  return apiRequest(`/dex/grid-bots/${encodeURIComponent(gridBotId)}/refill-prepare`, { method: 'POST', body: JSON.stringify(payload) });
}
export async function refillCommitDexGridBot(gridBotId, payload) {
  return apiRequest(`/dex/grid-bots/${encodeURIComponent(gridBotId)}/refill-commit`, { method: 'POST', body: JSON.stringify(payload) });
}

// ─── DEX TradingView Webhooks (Phase 4b) ────────────────────────────────────
// Signal from any chart → DEX swap. Pre-signed pool means alerts fire without
// the user's wallet being open. Public receiver lives at /api/dex/tv/{key}
// and is HMAC-validated against the webhook's secret.
export async function prepareDexTvWebhook(payload) {
  return apiRequest('/dex/tv-webhooks/prepare', { method: 'POST', body: JSON.stringify(payload) });
}
export async function commitDexTvWebhook(payload) {
  return apiRequest('/dex/tv-webhooks/commit', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexTvWebhooks() {
  return apiRequest('/dex/tv-webhooks');
}
export async function getDexTvWebhook(webhookKey) {
  return apiRequest(`/dex/tv-webhooks/${encodeURIComponent(webhookKey)}`);
}
export async function updateDexTvWebhook(webhookKey, payload) {
  return apiRequest(`/dex/tv-webhooks/${encodeURIComponent(webhookKey)}`, { method: 'PATCH', body: JSON.stringify(payload) });
}
export async function deleteDexTvWebhook(webhookKey) {
  return apiRequest(`/dex/tv-webhooks/${encodeURIComponent(webhookKey)}`, { method: 'DELETE' });
}
export async function refillPrepareDexTvWebhook(webhookKey, payload = {}) {
  return apiRequest(`/dex/tv-webhooks/${encodeURIComponent(webhookKey)}/refill-prepare`, { method: 'POST', body: JSON.stringify(payload) });
}
export async function refillCommitDexTvWebhook(webhookKey, payload) {
  return apiRequest(`/dex/tv-webhooks/${encodeURIComponent(webhookKey)}/refill-commit`, { method: 'POST', body: JSON.stringify(payload) });
}
export async function testDexTvWebhook(webhookKey, payload) {
  return apiRequest(`/dex/tv-webhooks/${encodeURIComponent(webhookKey)}/test`, { method: 'POST', body: JSON.stringify(payload) });
}

// ─── DEX Conditional Bot (Phase 6) ──────────────────────────────────────────
// Regime-switching orchestrator. Classifies BTC/ETH/SOL regime via shared
// OHLCV service; activates one inner DEX bot (Grid Bot / TV Webhook / DCA)
// per matching rule, pauses the others. Hysteresis built into the worker.
export async function createDexConditionalBot(payload) {
  return apiRequest('/dex/conditional-bots', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexConditionalBots() {
  return apiRequest('/dex/conditional-bots');
}
export async function listDexConditionalBotInnerBots() {
  // Sibling path (not under /conditional-bots/) to avoid an Azure-Functions
  // route collision with the /conditional-bots/{id} dynamic GET route.
  return apiRequest('/dex/conditional-bot-inventory');
}
export async function getDexConditionalBot(condBotId) {
  return apiRequest(`/dex/conditional-bots/${encodeURIComponent(condBotId)}`);
}
export async function updateDexConditionalBot(condBotId, payload) {
  return apiRequest(`/dex/conditional-bots/${encodeURIComponent(condBotId)}`, { method: 'PATCH', body: JSON.stringify(payload) });
}
export async function deleteDexConditionalBot(condBotId) {
  return apiRequest(`/dex/conditional-bots/${encodeURIComponent(condBotId)}`, { method: 'DELETE' });
}
export async function activateDexConditionalBot(condBotId) {
  return apiRequest(`/dex/conditional-bots/${encodeURIComponent(condBotId)}/activate`, { method: 'POST', body: JSON.stringify({}) });
}
export async function deactivateDexConditionalBot(condBotId) {
  return apiRequest(`/dex/conditional-bots/${encodeURIComponent(condBotId)}/deactivate`, { method: 'POST', body: JSON.stringify({}) });
}
export async function simulateDexConditionalBot(condBotId) {
  return apiRequest(`/dex/conditional-bots/${encodeURIComponent(condBotId)}/simulate`, { method: 'POST', body: JSON.stringify({}) });
}

// DCA pause/resume — used by Conditional Bot orchestrator, also exposed for
// direct user control if/when the DCA UI surfaces the action.
export async function pauseDexDcaPlan(planId) {
  return apiRequest(`/dex/dca/${encodeURIComponent(planId)}/pause`, { method: 'POST', body: JSON.stringify({}) });
}
export async function resumeDexDcaPlan(planId) {
  return apiRequest(`/dex/dca/${encodeURIComponent(planId)}/resume`, { method: 'POST', body: JSON.stringify({}) });
}

// ─── DEX Arbitrage Bot (Phase 7) ────────────────────────────────────────────
// Cross-DEX same-chain spread capture (CoW vs Velora). Pre-signed cycles,
// MEV-protected fills, loss-streak auto-pause. Worker polls quotes every
// minute and fires when net spread crosses the user's threshold.
export async function prepareDexArbBot(payload) {
  return apiRequest('/dex/arb-bots/prepare', { method: 'POST', body: JSON.stringify(payload) });
}
export async function commitDexArbBot(payload) {
  return apiRequest('/dex/arb-bots/commit', { method: 'POST', body: JSON.stringify(payload) });
}
export async function listDexArbBots() {
  return apiRequest('/dex/arb-bots');
}
export async function getDexArbBot(arbBotId) {
  return apiRequest(`/dex/arb-bots/${encodeURIComponent(arbBotId)}`);
}
export async function pauseDexArbBot(arbBotId) {
  return apiRequest(`/dex/arb-bots/${encodeURIComponent(arbBotId)}/pause`, { method: 'POST', body: JSON.stringify({}) });
}
export async function resumeDexArbBot(arbBotId) {
  return apiRequest(`/dex/arb-bots/${encodeURIComponent(arbBotId)}/resume`, { method: 'POST', body: JSON.stringify({}) });
}
export async function cancelDexArbBot(arbBotId) {
  return apiRequest(`/dex/arb-bots/${encodeURIComponent(arbBotId)}/cancel`, { method: 'POST', body: JSON.stringify({}) });
}
export async function scanDexArbBot(arbBotId) {
  return apiRequest(`/dex/arb-bots/${encodeURIComponent(arbBotId)}/scan`, { method: 'POST', body: JSON.stringify({}) });
}
export async function refillPrepareDexArbBot(arbBotId, payload = {}) {
  return apiRequest(`/dex/arb-bots/${encodeURIComponent(arbBotId)}/refill-prepare`, { method: 'POST', body: JSON.stringify(payload) });
}
export async function refillCommitDexArbBot(arbBotId, payload) {
  return apiRequest(`/dex/arb-bots/${encodeURIComponent(arbBotId)}/refill-commit`, { method: 'POST', body: JSON.stringify(payload) });
}

// Resolve any ERC-20 contract on a supported chain into { symbol, decimals,
// name } via on-chain calls. Backend caches results.
export async function resolveDexTokenInfo(chainId, address) {
  return apiRequest('/dex/token-info', { method: 'POST', body: JSON.stringify({ chainId, address }) });
}
